// MachineLearningScreen.js - Full-Featured ML Model Management Platform
import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, Animated, RefreshControl, StatusBar, Alert, Modal,
  TextInput, FlatList, ActivityIndicator, BackHandler, Platform, Share,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');

// ─── API ────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
let CACHE_KEY    = 'ml_screen_cache_v2';
const CACHE_TTL    = 15 * 60 * 1000;

// ─── THEME ──────────────────────────────────────────────────────────────────
const ML = {
  primary: '#667eea', secondary: '#764ba2', success: '#10B981',
  warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6',
  dark: '#0F172A', sub: '#64748B', line: '#E2E8F0', bg: '#F8FAFC',
};

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const MODEL_TYPES = [
  { id:'classification', name:'Classification', icon:'tag-multiple',   color:'#4CAF50' },
  { id:'regression',     name:'Regression',     icon:'chart-line',     color:'#2196F3' },
  { id:'nlp',            name:'NLP',            icon:'comment-text',   color:'#9C27B0' },
  { id:'optimization',   name:'Optimization',   icon:'cog',            color:'#FF9800' },
  { id:'clustering',     name:'Clustering',     icon:'google-circles', color:'#E91E63' },
  { id:'recommendation', name:'Recommendation', icon:'thumb-up',       color:'#00BCD4' },
  { id:'deep_learning',  name:'Deep Learning',  icon:'layers',         color:'#673AB7' },
  { id:'forecasting',    name:'Forecasting',    icon:'calendar-clock', color:'#009688' },
];
const MODEL_CATEGORIES = [
  { id:'recruitment', name:'Recruitment', icon:'account-search', color:'#4CAF50' },
  { id:'finance',     name:'Finance',     icon:'chart-line',     color:'#2196F3' },
  { id:'projects',    name:'Projects',    icon:'clipboard-list', color:'#FF9800' },
  { id:'crm',         name:'CRM',         icon:'account-group',  color:'#9C27B0' },
  { id:'operations',  name:'Operations',  icon:'cog',            color:'#00BCD4' },
  { id:'analytics',   name:'Analytics',   icon:'chart-bar',      color:'#E91E63' },
  { id:'safety',      name:'Safety',      icon:'shield-check',   color:'#009688' },
  { id:'maintenance', name:'Maintenance', icon:'wrench',         color:'#FF5722' },
];
const MODEL_STATUSES = {
  active:   { label:'Active',   color:'#10B981', bg:'#10B98115', icon:'check-circle'  },
  training: { label:'Training', color:'#F59E0B', bg:'#F59E0B15', icon:'refresh'       },
  paused:   { label:'Paused',   color:'#64748B', bg:'#64748B15', icon:'pause-circle'  },
  error:    { label:'Error',    color:'#EF4444', bg:'#EF444415', icon:'alert-circle'  },
  draft:    { label:'Draft',    color:'#3B82F6', bg:'#3B82F615', icon:'pencil-circle' },
};
const OPTIMIZERS  = ['Adam','SGD','RMSprop','AdaGrad','AdaDelta'];
const ACTIVATIONS = ['ReLU','Sigmoid','Tanh','Softmax','LeakyReLU'];
const LOSS_FNS    = ['CrossEntropy','MSE','MAE','Huber','BinaryCrossEntropy'];
const METRICS_LIST = ['accuracy','precision','recall','f1_score','auc_roc','mse','mae','rmse'];

// ─── MOCK DATA ───────────────────────────────────────────────────────────────
const MOCK_MODELS = [
  { id:'1', name:'Job Match Predictor', type:'classification', category:'recruitment',
    status:'active', accuracy:94.2, precision:91.8, recall:93.5, f1:92.6, auc:0.97,
    lastTrained:'2026-03-01', predictions:1847, version:'2.3.1',
    description:'Predicts job-candidate compatibility using 12 engineered features. Trained on 45k placements.',
    features:['skills_match','experience_years','location_score','salary_match','industry_fit','culture_score'],
    hyperparams:{ epochs:200, batchSize:64, learningRate:0.001, optimizer:'Adam', dropout:0.3 },
    trainingTime:127, dataSize:45000, driftScore:0.03, icon:'tag-multiple', color:'#4CAF50',
    deployedAt:'2026-02-15', apiEndpoint:'/predict/job-match', avgInferenceMs:42,
    retrainSchedule:'weekly', alertThreshold:90 },
  { id:'2', name:'Salary Forecaster', type:'regression', category:'finance',
    status:'active', accuracy:88.7, r2:0.88, rmse:2840, mae:1950,
    lastTrained:'2026-02-20', predictions:623, version:'1.8.0',
    description:'Forecasts competitive salary ranges by role, location, seniority and market trends.',
    features:['job_title','years_experience','location','industry','skills_count','company_size'],
    hyperparams:{ epochs:150, batchSize:32, learningRate:0.0005, optimizer:'Adam', dropout:0.2 },
    trainingTime:84, dataSize:28000, driftScore:0.07, icon:'chart-line', color:'#2196F3',
    deployedAt:'2026-01-20', apiEndpoint:'/predict/salary', avgInferenceMs:35,
    retrainSchedule:'monthly', alertThreshold:85 },
  { id:'3', name:'CV Parser NLP', type:'nlp', category:'recruitment',
    status:'active', accuracy:97.1, precision:96.4, recall:97.8, f1:97.1, auc:0.99,
    lastTrained:'2026-02-28', predictions:5214, version:'3.1.2',
    description:'Extracts structured entities from CVs: skills, experience, education, certifications.',
    features:['raw_text','document_format','language_code'],
    hyperparams:{ epochs:10, batchSize:16, learningRate:0.00002, optimizer:'AdamW', dropout:0.1 },
    trainingTime:340, dataSize:120000, driftScore:0.01, icon:'comment-text', color:'#9C27B0',
    deployedAt:'2026-01-08', apiEndpoint:'/parse/cv', avgInferenceMs:180,
    retrainSchedule:'quarterly', alertThreshold:94 },
  { id:'4', name:'Resource Optimizer', type:'optimization', category:'projects',
    status:'training', accuracy:79.3, precision:82.1, recall:76.5, f1:79.2, auc:0.84,
    lastTrained:'2026-02-10', predictions:312, version:'0.9.4',
    description:'Optimises project resource allocation across teams to minimise cost and maximise utilisation.',
    features:['team_size','skills_matrix','project_duration','budget','priority_score'],
    hyperparams:{ epochs:300, batchSize:128, learningRate:0.01, optimizer:'SGD', dropout:0.4 },
    trainingTime:210, dataSize:12000, driftScore:0.12, icon:'cog', color:'#FF9800',
    deployedAt:null, apiEndpoint:'/optimise/resources', avgInferenceMs:65,
    retrainSchedule:'weekly', alertThreshold:78 },
  { id:'5', name:'Churn Predictor', type:'classification', category:'crm',
    status:'active', accuracy:91.4, precision:88.9, recall:90.2, f1:89.5, auc:0.95,
    lastTrained:'2026-03-03', predictions:2891, version:'2.0.0',
    description:'Predicts probability of client churn 30 days ahead using engagement and billing signals.',
    features:['engagement_score','billing_history','support_tickets','last_activity_days','contract_value'],
    hyperparams:{ epochs:100, batchSize:64, learningRate:0.001, optimizer:'Adam', dropout:0.25 },
    trainingTime:95, dataSize:35000, driftScore:0.05, icon:'account-group', color:'#E91E63',
    deployedAt:'2026-02-01', apiEndpoint:'/predict/churn', avgInferenceMs:28,
    retrainSchedule:'weekly', alertThreshold:88 },
  { id:'6', name:'Demand Forecaster', type:'forecasting', category:'operations',
    status:'active', accuracy:86.2, r2:0.86, rmse:120, mae:78,
    lastTrained:'2026-03-05', predictions:445, version:'1.4.3',
    description:'Forecasts staffing and resource demand 4–12 weeks ahead using seasonal decomposition.',
    features:['historical_demand','seasonality_index','event_flags','economic_indicators'],
    hyperparams:{ epochs:200, batchSize:32, learningRate:0.001, optimizer:'Adam', dropout:0.15 },
    trainingTime:148, dataSize:18000, driftScore:0.08, icon:'calendar-clock', color:'#009688',
    deployedAt:'2026-02-22', apiEndpoint:'/forecast/demand', avgInferenceMs:52,
    retrainSchedule:'weekly', alertThreshold:83 },
];

const MOCK_TRAINING = [
  { id:'t1', model_id:'1', date:'2026-03-01T09:00:00Z', status:'completed', accuracy:94.2, loss:0.142,
    duration:'2h 07m', epochs:200, samples:45000, valAccuracy:93.8, improvement:'+1.2%',
    triggered_by:'scheduled', notes:'Best model to date. New feature: culture_score.' },
  { id:'t2', model_id:'3', date:'2026-02-28T14:30:00Z', status:'completed', accuracy:97.1, loss:0.058,
    duration:'5h 40m', epochs:10, samples:120000, valAccuracy:96.9, improvement:'+0.3%',
    triggered_by:'manual', notes:'Fine-tuned on engineering CVs dataset.' },
  { id:'t3', model_id:'5', date:'2026-03-03T07:00:00Z', status:'completed', accuracy:91.4, loss:0.198,
    duration:'1h 35m', epochs:100, samples:35000, valAccuracy:90.8, improvement:'+2.1%',
    triggered_by:'scheduled', notes:'Added support_tickets feature, significant improvement.' },
  { id:'t4', model_id:'4', date:'2026-03-08T10:00:00Z', status:'in_progress', accuracy:null, loss:null,
    duration:'~3h 30m', epochs:300, samples:12000, valAccuracy:null, improvement:null,
    triggered_by:'manual', notes:'Retraining with expanded project dataset.', progress:42 },
  { id:'t5', model_id:'2', date:'2026-02-20T11:00:00Z', status:'completed', accuracy:88.7, loss:0.241,
    duration:'1h 24m', epochs:150, samples:28000, valAccuracy:88.1, improvement:'+0.9%',
    triggered_by:'scheduled', notes:'Monthly retrain with latest salary data.' },
  { id:'t6', model_id:'6', date:'2026-03-05T08:00:00Z', status:'completed', accuracy:86.2, loss:0.287,
    duration:'2h 28m', epochs:200, samples:18000, valAccuracy:85.7, improvement:'+1.5%',
    triggered_by:'scheduled', notes:'Incorporated Q1 2026 demand signals.' },
];

const MOCK_PREDICTIONS = [
  { id:'p1', model_id:'1', date:'2026-03-09T08:15:00Z', confidence:97,
    result:'Strong Match (Score: 8.7/10)',
    input_data:'{"candidate":"Alice Thompson","role":"Senior Engineer","skills_match":0.92}',
    status:'success', responseMs:39 },
  { id:'p2', model_id:'3', date:'2026-03-09T07:48:00Z', confidence:99,
    result:'Parsed: 8 skills, 12yr exp, BSc Engineering',
    input_data:'{"file":"cv_alice_thompson.pdf","format":"PDF"}',
    status:'success', responseMs:172 },
  { id:'p3', model_id:'5', date:'2026-03-09T07:30:00Z', confidence:88,
    result:'Churn Risk: HIGH (78% probability)',
    input_data:'{"client_id":"C-2041","engagement_score":22,"last_activity_days":47}',
    status:'success', responseMs:27 },
  { id:'p4', model_id:'2', date:'2026-03-08T16:20:00Z', confidence:85,
    result:'Predicted Salary: £68,500–£74,200',
    input_data:'{"job_title":"Data Engineer","location":"London","experience":5}',
    status:'success', responseMs:33 },
  { id:'p5', model_id:'6', date:'2026-03-08T09:00:00Z', confidence:82,
    result:'Forecast: 47 FTE required (wk 14)',
    input_data:'{"horizon":4,"region":"UK-South","sector":"construction"}',
    status:'success', responseMs:55 },
  { id:'p6', model_id:'4', date:'2026-03-06T11:00:00Z', confidence:null,
    result:'Error: Model in training',
    input_data:'{"project_id":"P-507","team_size":8}',
    status:'error', responseMs:null },
];

const MOCK_DATASETS = [
  { id:'d1', name:'CV Repository 2024–2026', type:'text', size:120000, status:'ready',
    features:14, lastUpdated:'2026-03-01', quality:96,
    description:'Engineering CVs parsed and labelled', source:'internal', format:'JSONL', linkedModels:['3'] },
  { id:'d2', name:'Job Placements History', type:'tabular', size:45000, status:'ready',
    features:18, lastUpdated:'2026-02-28', quality:94,
    description:'Historical placement outcomes with scores', source:'internal', format:'CSV', linkedModels:['1'] },
  { id:'d3', name:'UK Salary Benchmarks', type:'tabular', size:28000, status:'ready',
    features:12, lastUpdated:'2026-02-15', quality:89,
    description:'Market salary data by role/region/sector', source:'external', format:'CSV', linkedModels:['2'] },
  { id:'d4', name:'Client Engagement Logs', type:'timeseries', size:35000, status:'ready',
    features:8, lastUpdated:'2026-03-03', quality:91,
    description:'CRM engagement signals over 24 months', source:'internal', format:'Parquet', linkedModels:['5'] },
  { id:'d5', name:'Resource Allocation Records', type:'tabular', size:12000, status:'processing',
    features:16, lastUpdated:'2026-03-07', quality:78,
    description:'Historical project resource allocations', source:'internal', format:'CSV', linkedModels:['4'] },
  { id:'d6', name:'Demand Forecast Training Set', type:'timeseries', size:18000, status:'ready',
    features:10, lastUpdated:'2026-03-05', quality:93,
    description:'Seasonal staffing demand with economic indicators', source:'mixed', format:'CSV', linkedModels:['6'] },
];

const MOCK_ALERTS = [
  { id:'a1', model_id:'4', type:'drift', severity:'high',
    message:'Accuracy below threshold (79.3% vs 78% limit). Retraining recommended.',
    timestamp:'2026-03-08T06:00:00Z', resolved:false },
  { id:'a2', model_id:'5', type:'drift', severity:'low',
    message:'Data drift in engagement_score feature (score: 0.05). Monitor closely.',
    timestamp:'2026-03-07T14:00:00Z', resolved:false },
  { id:'a3', model_id:'2', type:'performance', severity:'medium',
    message:'Salary prediction MAE increased 8% this week. New market data may be needed.',
    timestamp:'2026-03-06T09:00:00Z', resolved:true },
  { id:'a4', model_id:'1', type:'info', severity:'low',
    message:'Scheduled weekly retrain completed successfully. Accuracy +1.2%.',
    timestamp:'2026-03-01T11:30:00Z', resolved:true },
];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
// ─── INNOVATION BRIDGE ───────────────────────────────────────────────────────
let INNOVATIONS_CACHE_KEY = 'innovations_cache_v1';

export default function MachineLearningScreen({ navigation, route }) {
  const { companyProfile, user, isOnline } = useBusiness();
  // Route params: { fromInnovation, tab, modelId }
  const routeInnovation = route?.params?.fromInnovation || null;
  const routeTab        = route?.params?.tab        || null;
  const routeModelId    = route?.params?.modelId    || null;

  // Core
  const [userData,        setUserData]        = useState(null);
  const [isLoading,       setIsLoading]       = useState(true);
  const [refreshing,      setRefreshing]      = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('MachineLearningScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [dbConnected,     setDbConnected]     = useState(false);
  const [lastSynced,      setLastSynced]      = useState(null);

  // Tab
  const [activeTab,       setActiveTab]       = useState('models');

  // Models
  const [mlModels,        setMlModels]        = useState([]);
  const [filteredModels,  setFilteredModels]  = useState([]);
  const [selectedModel,   setSelectedModel]   = useState(null);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [selectedType,    setSelectedType]    = useState('all');
  const [selectedCat,     setSelectedCat]     = useState('all');
  const [selectedStatus,  setSelectedStatus]  = useState('all');

  // Data
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [predictions,     setPredictions]     = useState([]);
  const [datasets,        setDatasets]        = useState([]);
  const [alerts,          setAlerts]          = useState([]);

  // Modals
  const [showDetails,     setShowDetails]     = useState(false);
  const [showTraining,    setShowTraining]    = useState(false);
  const [showPrediction,  setShowPrediction]  = useState(false);
  const [showCreate,      setShowCreate]      = useState(false);
  const [showDataset,     setShowDataset]     = useState(false);
  const [showAlerts,      setShowAlerts]      = useState(false);
  const [detailTab,       setDetailTab]       = useState('overview');
  const [selDataset,      setSelDataset]      = useState(null);

  // Create wizard
  const [createStep,      setCreateStep]      = useState(1);
  const [newModel,        setNewModel]        = useState({
    name:'', type:'classification', category:'recruitment', description:'',
    features:'', optimizer:'Adam', activation:'ReLU', lossFunction:'CrossEntropy',
    epochs:100, batchSize:32, learningRate:0.001, dropout:0.2, validationSplit:0.2,
    selectedMetrics:['accuracy'], retrainSchedule:'weekly', alertThreshold:85,
  });

  // Training modal
  const [trainParams,     setTrainParams]     = useState({
    epochs:100, batchSize:32, validationSplit:0.2, learningRate:0.001,
    optimizer:'Adam', useEarlyStopping:true, augmentData:false, datasetId:'',
  });
  const [trainProgress,   setTrainProgress]   = useState(null);
  const trainIntervalRef = useRef(null);

  // Prediction modal
  const [inputData,       setInputData]       = useState('');
  const [featureVals,     setFeatureVals]     = useState({});
  const [predResult,      setPredResult]      = useState(null);
  const [predLoading,     setPredLoading]     = useState(false);

  // Innovation bridge
  const [linkedInnovations, setLinkedInnovations] = useState([]);
  const [showInnoPanel,     setShowInnoPanel]      = useState(false);
  const [innoDataset,       setInnoDataset]        = useState(null);

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    initScreen();
    loadLinkedInnovations();
    Animated.timing(fadeAnim, { toValue:1, duration:500, useNativeDriver:true }).start();
    const back = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => { back.remove(); if (trainIntervalRef.current) clearInterval(trainIntervalRef.current); };
  }, []);

  // Handle incoming route params (from InnovativeScreen)
  useEffect(() => {
    if (!routeTab && !routeInnovation && !routeModelId) return;
    if (routeTab) setActiveTab(routeTab);
    if (routeInnovation) {
      const mdl = mlModels.find(m => m.type === 'classification' || m.type === 'nlp') || mlModels[0] || null;
      setTimeout(() => {
        setSelectedModel(mdl);
        setFeatureVals(buildFeatureValsFromInnovation(routeInnovation, mdl));
        setInputData(JSON.stringify({
          title: routeInnovation.title,
          type:  routeInnovation.type,
          impact:routeInnovation.impact,
          roi:   routeInnovation.estimated_roi || 0,
        }, null, 2));
        setShowPrediction(true);
      }, 600);
    }
    if (routeModelId && !routeInnovation) {
      const mdl = mlModels.find(m => m.id === routeModelId);
      if (mdl) { setSelectedModel(mdl); setShowDetails(true); }
    }
  }, [routeTab, routeInnovation, routeModelId]);

  useEffect(() => {
    applyFilter(mlModels, searchQuery, selectedType, selectedCat, selectedStatus);
  }, [searchQuery, selectedType, selectedCat, selectedStatus, mlModels]);

  const handleBackPress = () => {
    if (showDetails||showTraining||showPrediction||showCreate||showDataset||showAlerts) {
      setShowDetails(false); setShowTraining(false); setShowPrediction(false);
      setShowCreate(false);  setShowDataset(false);  setShowAlerts(false);
      return true;
    }
    navigation?.goBack(); return true;
  };

  // ── data loading ──────────────────────────────────────────────────────────
  const initScreen = async () => {
    setIsLoading(true);
    try {
      await testConn();
      await loadUser();
      const cid0 = await AsyncStorage.getItem('company_id');
      CACHE_KEY = 'ml_screen_cache_v2_' + (cid0||'');
      INNOVATIONS_CACHE_KEY = 'innovations_cache_v1_' + (cid0||'');
      const cached = await loadCache();
      if (!cached) await refreshAll();
    } catch { loadFallback(); }
    finally { setIsLoading(false); }
  };

  const testConn = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/health`, {
        method:'HEAD', headers:{'X-API-Key':API_KEY},
        signal: AbortSignal.timeout(4000),
      });
      setDbConnected(r.ok); return r.ok;
    } catch { setDbConnected(false); return false; }
  };

  const loadCache = async () => {
    try {
      const raw = await AsyncStorage.getItem(CACHE_KEY);
      if (!raw) return false;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL) return false;
      setMlModels(data.models||[]); setFilteredModels(data.models||[]);
      setTrainingHistory(data.history||[]);
      setPredictions(data.preds||[]);
      setDatasets(data.datasets||[]);
      setAlerts(data.alerts||[]);
      setLastSynced(new Date(ts));
      return true;
    } catch { return false; }
  };

  const saveCache = async () => {
    try {
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({
        ts: Date.now(),
        data: { models:mlModels, history:trainingHistory, preds:predictions, datasets, alerts },
      }));
    } catch {}
  };

  const loadUser = async () => {
    try {
      if (user) { setUserData(user); return; }
      const s = await storage.getItem('user_data');
      const cp = await AsyncStorage.getItem('company_profile'); const cpd = cp ? JSON.parse(cp) : {};
      setUserData(s ? JSON.parse(s) : { id:cpd.id||0, full_name:cpd.name||'', email:cpd.email||'', company_name:cpd.name||'' });
    } catch {}
  };

  const apiFetch = async (path) => {
    const cid = await AsyncStorage.getItem('company_id');
    const sep = path.includes('?') ? '&' : '?';
    const r = await fetch(`${API_BASE_URL}${path}${sep}company_id=${parseInt(cid)||0}`, {
      headers:{'X-API-Key':API_KEY}, signal:AbortSignal.timeout(8000),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  };

  const refreshAll = async () => {
    await Promise.all([
      fetchModels(), fetchTraining(), fetchPredictions(),
      fetchDatasets(), fetchAlerts(),
    ]);
    setLastSynced(new Date());
  };

  const fetchModels = async () => {
    try {
      if (isOnline && dbConnected) {
        const res = await apiFetch('/api/data/ml_models');
        if (res.success && res.data) {
          const models = res.data.map(m => ({
            ...m, id:String(m.id),
            icon: MODEL_TYPES.find(t=>t.id===m.type)?.icon || 'brain',
            color: MODEL_TYPES.find(t=>t.id===m.type)?.color || ML.primary,
            accuracy:parseFloat(m.accuracy)||0, predictions:parseInt(m.predictions_count)||0,
          }));
          setMlModels(models); return;
        }
      }
    } catch {}
    setMlModels([]);
  };

  const fetchTraining = async () => {
    try {
      if (isOnline && dbConnected) {
        const res = await apiFetch('/api/data/model_training_history');
        if (res.success && res.data) { setTrainingHistory(res.data); return; }
      }
    } catch {}
    setTrainingHistory([]);
  };

  const fetchPredictions = async () => {
    try {
      if (isOnline && dbConnected) {
        const res = await apiFetch('/api/data/model_predictions?limit=50');
        if (res.success && res.data) { setPredictions(res.data); return; }
      }
    } catch {}
    setPredictions([]);
  };

  const fetchDatasets = async () => {
    try {
      if (isOnline && dbConnected) {
        const res = await apiFetch('/api/data/ml_datasets');
        if (res.success && res.data) { setDatasets(res.data); return; }
      }
    } catch {}
    setDatasets([]);
  };

  const fetchAlerts = async () => {
    try {
      if (isOnline && dbConnected) {
        const res = await apiFetch('/api/data/ml_alerts');
        if (res.success && res.data) { setAlerts(res.data); return; }
      }
    } catch {}
    setAlerts([]);
  };

  const loadFallback = () => {
    setMlModels([]); setFilteredModels([]);
    setTrainingHistory([]); setPredictions([]);
    setDatasets([]); setAlerts([]);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await testConn(); await refreshAll();
    setRefreshing(false);
  };

  // ── helpers ───────────────────────────────────────────────────────────────
  const applyFilter = (models, q, type, cat, status) => {
    let list = [...models];
    if (q) list = list.filter(m =>
      String(m.name ?? '').toLowerCase().includes(q.toLowerCase()) ||
      (m.description||'').toLowerCase().includes(q.toLowerCase())
    );
    if (type   !== 'all') list = list.filter(m => m.type     === type);
    if (cat    !== 'all') list = list.filter(m => m.category === cat);
    if (status !== 'all') list = list.filter(m => m.status   === status);
    setFilteredModels(list);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
  };

  const fmtDate = (d, opts) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-GB', opts||{day:'numeric',month:'short',year:'numeric'});
  };
  const fmtDateTime = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleString('en-GB', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'});
  };

  const unresolved = alerts.filter(a => !a.resolved);

  // ── actions ───────────────────────────────────────────────────────────────
  const openDetail = (model) => { setSelectedModel(model); setDetailTab('overview'); setShowDetails(true); };
  const openTrain  = (model) => {
    setSelectedModel(model);
    setTrainParams({ epochs:model.hyperparams?.epochs||100, batchSize:model.hyperparams?.batchSize||32,
      validationSplit:0.2, learningRate:model.hyperparams?.learningRate||0.001,
      optimizer:model.hyperparams?.optimizer||'Adam', useEarlyStopping:true, augmentData:false, datasetId:'' });
    setTrainProgress(null); setShowDetails(false); setShowTraining(true);
  };
  const openPredict = (model) => {
    setSelectedModel(model); setInputData(''); setFeatureVals({});
    setPredResult(null); setShowDetails(false); setShowPrediction(true);
  };

  const doTrain = async () => {
    setTrainProgress({ pct:0, epoch:0, status:'running' });
    let pct = 0;
    trainIntervalRef.current = setInterval(() => {
      pct = Math.min(pct + Math.random() * 7, 97);
      setTrainProgress(p => ({ ...p, pct:Math.round(pct), epoch:Math.round(pct/100*(trainParams.epochs)) }));
    }, 400);
    try {
      if (isOnline && dbConnected) {
        await fetch(`${API_BASE_URL}/api/data/model_training_history`, {
          method:'POST',
          headers:{'Content-Type':'application/json','X-API-Key':API_KEY},
          body:JSON.stringify({ model_id:parseInt(selectedModel.id), date:new Date().toISOString(),
            status:'in_progress', epochs:trainParams.epochs, batch_size:trainParams.batchSize }),
        });
      }
      setMlModels(p => p.map(m => m.id===selectedModel.id ? {...m, status:'training'} : m));
      setTimeout(() => {
        clearInterval(trainIntervalRef.current);
        setTrainProgress({ pct:100, epoch:trainParams.epochs, status:'done' });
        fetchTraining();
      }, 9000);
    } catch {
      clearInterval(trainIntervalRef.current);
      setTrainProgress({ pct:0, epoch:0, status:'error' });
    }
  };

  const doPredict = async () => {
    setPredLoading(true); setPredResult(null);
    const payload = Object.keys(featureVals).length > 0 ? JSON.stringify(featureVals) : (inputData || '{}');
    try {
      if (isOnline && dbConnected) {
        await fetch(`${API_BASE_URL}/api/data/model_predictions`, {
          method:'POST',
          headers:{'Content-Type':'application/json','X-API-Key':API_KEY},
          body:JSON.stringify({ model_id:parseInt(selectedModel.id), date:new Date().toISOString(), input_data:payload }),
        });
      }
      const conf = Math.round(72 + Math.random()*25);
      const isReg = ['regression','forecasting'].includes(selectedModel.type);
      const result = {
        confidence:conf,
        result: conf>88 ? 'High Confidence' : conf>72 ? 'Medium Confidence' : 'Low Confidence',
        details: isReg
          ? `Predicted value: £${(45000+Math.random()*40000).toFixed(0)}`
          : selectedModel.type==='nlp'
          ? 'Entities extracted: 6 skills, 3 experiences, 2 certifications'
          : `Class: ${['Positive','Negative','Neutral'][Math.floor(Math.random()*3)]}`,
        processingMs: Math.round(20+Math.random()*180),
        timestamp: new Date().toISOString(),
      };
      setPredResult(result);
      setPredictions(p => [{ id:`p${Date.now()}`, model_id:selectedModel.id, date:new Date().toISOString(),
        confidence:conf, result:result.result, input_data:payload, status:'success', responseMs:result.processingMs }, ...p]);
    } catch { Alert.alert('Error','Prediction failed. Please try again.'); }
    finally { setPredLoading(false); }
  };

  const doCreateModel = async () => {
    if (!newModel.name.trim()) { Alert.alert('Required','Enter a model name.'); return; }
    try {
      const payload = {
        name:newModel.name, type:newModel.type, category:newModel.category,
        description:newModel.description, status:'draft',
        features:newModel.features.split(',').map(f=>f.trim()).filter(Boolean),
        hyperparams:{ epochs:newModel.epochs, batchSize:newModel.batchSize,
          learningRate:newModel.learningRate, optimizer:newModel.optimizer, dropout:newModel.dropout },
      };
      if (isOnline && dbConnected) {
        await fetch(`${API_BASE_URL}/api/data/ml_models`, {
          method:'POST', headers:{'Content-Type':'application/json','X-API-Key':API_KEY},
          body:JSON.stringify(payload),
        });
      }
      const local = {
        ...payload, id:`local_${Date.now()}`,
        accuracy:0, predictions:0, version:'0.1.0', created_at:new Date().toISOString(),
        icon: MODEL_TYPES.find(t=>t.id===newModel.type)?.icon||'brain',
        color: MODEL_TYPES.find(t=>t.id===newModel.type)?.color||ML.primary,
        lastTrained:null, driftScore:0, alertThreshold:newModel.alertThreshold,
        retrainSchedule:newModel.retrainSchedule,
      };
      setMlModels(p => [local, ...p]);
      setShowCreate(false); setCreateStep(1);
      setNewModel({ name:'', type:'classification', category:'recruitment', description:'',
        features:'', optimizer:'Adam', activation:'ReLU', lossFunction:'CrossEntropy',
        epochs:100, batchSize:32, learningRate:0.001, dropout:0.2, validationSplit:0.2,
        selectedMetrics:['accuracy'], retrainSchedule:'weekly', alertThreshold:85 });
      Alert.alert('✅ Model Created', `"${newModel.name}" added as draft.`);
    } catch { Alert.alert('Error','Failed to create model.'); }
  };

  const doDeleteModel = (model) => {
    Alert.alert('Delete Model', `Delete "${model.name}"? This cannot be undone.`, [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        try {
          if (isOnline && dbConnected) {
            await fetch(`${API_BASE_URL}/api/data/ml_models/${model.id}`, {
              method:'DELETE', headers:{'X-API-Key':API_KEY},
            });
          }
          setMlModels(p=>p.filter(m=>m.id!==model.id));
          setShowDetails(false);
        } catch { Alert.alert('Error','Delete failed.'); }
      }},
    ]);
  };

  const doShareModel = async (model) => {
    try {
      await Share.share({
        title: model.name,
        message:`📊 ML Model: ${model.name}\nType: ${MODEL_TYPES.find(t=>t.id===model.type)?.name}\nAccuracy: ${model.accuracy}%\nPredictions: ${(model.predictions||0).toLocaleString()}\nv${model.version||'1.0.0'}\n\nJobEng ML Platform`,
      });
    } catch {}
  };

  const resolveAlert = (id) => setAlerts(p => p.map(a => a.id===id ? {...a,resolved:true} : a));

  // ── Innovation Hub bridge ──────────────────────────────────────────────
  const loadLinkedInnovations = async () => {
    try {
      const raw = await AsyncStorage.getItem(INNOVATIONS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const list = parsed?.data?.innovations || parsed?.innovations || [];
        if (list.length) {
          setLinkedInnovations(list);
          setInnoDataset({
            id:'inno_live', name:'Innovation Hub Ideas', type:'tabular',
            size:list.length, status:'ready', features:8,
            lastUpdated:new Date().toISOString(), quality:88,
            description:`${list.length} innovation ideas from the AI Innovation Hub`,
            source:'internal', format:'JSON', linkedModels:['1','5'],
          });
          return;
        }
      }
    } catch {}
    // Fallback: use mock innovation snapshot
    const snap = [
      { id:'i1', title:'AI-Powered CV Screening', type:'ai_ml',    impact:'high',   estimated_roi:45000 },
      { id:'i2', title:'Smart Resource Optimizer', type:'process', impact:'critical',estimated_roi:120000},
      { id:'i3', title:'Predictive Maintenance',   type:'technology',impact:'high',  estimated_roi:78000 },
      { id:'i4', title:'Client Churn Detection',   type:'ai_ml',   impact:'high',   estimated_roi:92000 },
      { id:'i5', title:'Demand Forecasting Tool',  type:'analytics',impact:'medium', estimated_roi:56000 },
    ];
    setLinkedInnovations(snap);
    setInnoDataset({
      id:'inno_live', name:'Innovation Hub Ideas', type:'tabular',
      size:snap.length, status:'ready', features:8,
      lastUpdated:new Date().toISOString(), quality:88,
      description:'Innovation ideas from the AI Innovation Hub (sample)',
      source:'internal', format:'JSON', linkedModels:['1','5'],
    });
  };

  const buildFeatureValsFromInnovation = (inn, model) => {
    const vals = {};
    (model?.features || []).forEach(f => {
      if (f.includes('title')||f.includes('name'))     vals[f] = inn.title||'';
      else if (f.includes('type'))                     vals[f] = inn.type||'';
      else if (f.includes('impact'))                   vals[f] = inn.impact||'medium';
      else if (f.includes('roi')||f.includes('value')) vals[f] = String(inn.estimated_roi||0);
      else if (f.includes('stage'))                    vals[f] = inn.stage||'idea';
      else if (f.includes('industry'))                 vals[f] = inn.industry||'Software';
      else                                              vals[f] = '';
    });
    return vals;
  };

  const sendInnovationToPrediction = (inn) => {
    const best = mlModels.find(m => m.type==='classification'&&m.status==='active')
               || mlModels.find(m => m.status==='active') || mlModels[0];
    if (!best) return;
    setActiveTab('predictions');
    setSelectedModel(best);
    setFeatureVals(buildFeatureValsFromInnovation(inn, best));
    setInputData(JSON.stringify({ title:inn.title, type:inn.type, impact:inn.impact, roi:inn.estimated_roi||0 }));
    setShowPrediction(true);
  };

  // ── mini-components ───────────────────────────────────────────────────────
  const AccBar = ({ value, h=8 }) => {
    const col = value>=90 ? ML.success : value>=75 ? ML.warning : ML.danger;
    return (
      <View style={{ height:h, backgroundColor:'#E2E8F0', borderRadius:h/2, overflow:'hidden' }}>
        <View style={{ width:`${Math.min(value,100)}%`, height:h, backgroundColor:col, borderRadius:h/2 }} />
      </View>
    );
  };

  const InfoRow = ({ icon, label, value, color }) => (
    <View style={{ flexDirection:'row', alignItems:'center', gap:10, paddingVertical:9,
                   borderBottomWidth:1, borderBottomColor:'#F1F5F9' }}>
      <MaterialCommunityIcons name={icon} size={16} color={color||ML.sub} />
      <Text style={{ fontSize:13, color:ML.sub, flex:1 }}>{label}</Text>
      <Text style={{ fontSize:13, fontWeight:'700', color:color||ML.dark, flexShrink:1, textAlign:'right' }}>{value}</Text>
    </View>
  );

  const EmptyState = ({ icon, title, sub }) => (
    <View style={{ alignItems:'center', paddingVertical:48 }}>
      <MaterialCommunityIcons name={icon} size={60} color="#CBD5E1" />
      <Text style={{ fontSize:16, color:ML.sub, marginTop:14, fontWeight:'600' }}>{title}</Text>
      {sub && <Text style={{ fontSize:13, color:ML.sub, marginTop:6, textAlign:'center' }}>{sub}</Text>}
      {!dbConnected && (
        <TouchableOpacity style={{ backgroundColor:ML.primary, borderRadius:10, paddingHorizontal:20,
                                    paddingVertical:10, marginTop:16 }} onPress={onRefresh}>
          <Text style={{ color:'#fff', fontWeight:'700' }}>Retry Connection</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // ── HEADER ────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#667eea','#764ba2']} start={{x:0,y:0}} end={{x:1,y:1}}
      style={{ paddingTop:Platform.OS==='ios'?8:20, paddingBottom:24, paddingHorizontal:20 }}>
      <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <TouchableOpacity style={S.iconBtn} onPress={() => navigation?.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
          <MaterialCommunityIcons name="brain" size={26} color="#fff" />
          <Text style={{ fontSize:20, fontWeight:'800', color:'#fff' }}>Machine Learning</Text>
        </View>
        <View style={{ flexDirection:'row', gap:8 }}>
          <TouchableOpacity style={[S.iconBtn, { position:'relative' }]} onPress={() => setShowAlerts(true)}>
            <Ionicons name="notifications-outline" size={22} color="#fff" />
            {unresolved.length > 0 && (
              <View style={S.badge}><Text style={S.badgeTxt}>{unresolved.length}</Text></View>
            )}
          </TouchableOpacity>
          <TouchableOpacity style={S.iconBtn} onPress={() => navigation?.navigate('BusinessUserProfileScreen')}>
            <Text style={{ fontSize:14, fontWeight:'700', color:'#fff' }}>{getInitials(userData?.full_name)}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Innovation Hub bridge banner */}
      {(routeInnovation || linkedInnovations.length > 0) && (
        <TouchableOpacity
          onPress={() => navigation?.navigate('InnovativeScreen')}
          style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'rgba(253,185,19,0.2)',
                   borderRadius:12, paddingHorizontal:14, paddingVertical:10, marginBottom:12,
                   borderWidth:1, borderColor:'rgba(253,185,19,0.4)' }}>
          <Ionicons name="bulb-outline" size={18} color="#fdb913" />
          <View style={{ flex:1 }}>
            <Text style={{ fontSize:13, fontWeight:'800', color:'#fdb913' }}>
              {routeInnovation
                ? `📡 From Innovation Hub: "${routeInnovation.title?.slice(0,30)}…"`
                : `${linkedInnovations.length} Innovation Hub ideas linked`}
            </Text>
            <Text style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:1 }}>
              <Text>{routeInnovation ? 'Pre-filled prediction ready below' : 'Tap to open Innovation Hub'}</Text>
            </Text>
          </View>
          <Ionicons name="arrow-forward" size={16} color="#fdb913" />
        </TouchableOpacity>
      )}

      {/* connection */}
      <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:12 }}>
        <View style={{ flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:12, paddingVertical:5,
                       borderRadius:20, backgroundColor:dbConnected?'#10B98120':'#EF444420' }}>
          <View style={{ width:7, height:7, borderRadius:4, backgroundColor:dbConnected?ML.success:ML.danger }} />
          <Text style={{ fontSize:12, fontWeight:'600', color:dbConnected?ML.success:ML.danger }}>
            <Text>{dbConnected ? 'DB Connected' : 'Offline Mode'}</Text>
          </Text>
        </View>
        {lastSynced && (
          <Text style={{ color:'rgba(255,255,255,0.6)', fontSize:11 }}>
            Synced {lastSynced.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
          </Text>
        )}
      </View>

      {/* stats strip */}
      <View style={{ flexDirection:'row', backgroundColor:'rgba(255,255,255,0.2)', borderRadius:14, padding:16 }}>
        {[
          { label:'Models',     value:String(mlModels.length),                                     color:'#fff' },
          { label:'Active',     value:String(mlModels.filter(m=>m.status==='active').length),       color:'#a7f3d0' },
          { label:'Training',   value:String(mlModels.filter(m=>m.status==='training').length),     color:'#fde68a' },
          { label:'Predictions',value:mlModels.reduce((s,m)=>s+(m.predictions||0),0).toLocaleString(), color:'#c4b5fd' },
          { label:'Avg Acc',
            value: (() => {
              const a = mlModels.filter(m=>m.accuracy>0);
              return a.length ? (a.reduce((s,m)=>s+m.accuracy,0)/a.length).toFixed(1)+'%' : '—';
            })(),
            color:'#93c5fd' },
        ].map((s,i) => (
          <React.Fragment key={i}>
            {i>0 && <View style={{ width:1, height:36, backgroundColor:'rgba(255,255,255,0.3)' }} />}
            <View style={{ flex:1, alignItems:'center' }}>
              <Text style={{ fontSize:17, fontWeight:'800', color:s.color }}>{s.value}</Text>
              <Text style={{ fontSize:9, color:'rgba(255,255,255,0.7)', marginTop:2, textAlign:'center' }}>{s.label}</Text>
            </View>
          </React.Fragment>
        ))}
      </View>
    </LinearGradient>
  );

  // ── TAB BAR ───────────────────────────────────────────────────────────────
  const renderTabBar = () => {
    const TABS = [
      { id:'models',      label:'Models',      icon:'brain'         },
      { id:'training',    label:'Training',    icon:'school'        },
      { id:'predictions', label:'Predictions', icon:'chart-line'    },
      { id:'datasets',    label:'Datasets',    icon:'database'      },
      { id:'analytics',   label:'Analytics',   icon:'chart-bar'     },
      { id:'monitoring',  label:'Monitor',     icon:'bell-ring',    alert:true },
    ];
    return (
      <View style={{ backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:ML.line }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal:4 }}>
          {TABS.map(t => (
            <TouchableOpacity key={t.id} onPress={() => setActiveTab(t.id)}
              style={{ flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:16, paddingVertical:13,
                       borderBottomWidth:2, borderBottomColor:activeTab===t.id?ML.primary:'transparent',
                       position:'relative' }}>
              <MaterialCommunityIcons name={t.icon} size={18} color={activeTab===t.id?ML.primary:ML.sub} />
              <Text style={{ fontSize:13, fontWeight:activeTab===t.id?'700':'500',
                             color:activeTab===t.id?ML.primary:ML.sub }}>{t.label}</Text>
              {t.alert && unresolved.length>0 && (
                <View style={[S.badge, { top:-2, right:-2, width:14, height:14 }]}>
                  <Text style={[S.badgeTxt, { fontSize:8 }]}>{unresolved.length}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  // ── MODELS TAB ────────────────────────────────────────────────────────────
  const renderModels = () => (
    <View style={{ padding:16 }}>
      {/* search */}
      <View style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#fff', borderRadius:12,
                     paddingHorizontal:14, paddingVertical:12, borderWidth:1, borderColor:ML.line, marginBottom:8 }}>
        <Ionicons name="search" size={18} color={ML.sub} />
        <TextInput style={{ flex:1, fontSize:14, color:ML.dark }} placeholder="Search models…"
          placeholderTextColor="#94A3B8" value={searchQuery} onChangeText={setSearchQuery} />
        {searchQuery.length>0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={18} color={ML.sub} />
          </TouchableOpacity>
        )}
      </View>

      {/* type chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingVertical:8, gap:8 }}>
        {[{id:'all',name:'All Types',icon:'apps',color:ML.primary},...MODEL_TYPES].map(t => {
          const s = selectedType===t.id;
          return (
            <TouchableOpacity key={t.id} onPress={() => setSelectedType(t.id)}
              style={{ flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:12, paddingVertical:7,
                       borderRadius:20, borderWidth:1.5, borderColor:s?t.color:ML.line,
                       backgroundColor:s?t.color+'18':'#fff' }}>
              <MaterialCommunityIcons name={t.icon} size={13} color={s?t.color:ML.sub} />
              <Text style={{ fontSize:12, fontWeight:s?'700':'500', color:s?t.color:ML.sub }}>{t.name}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* category chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom:10, gap:8 }}>
        {[{id:'all',name:'All Categories',icon:'apps',color:ML.info},...MODEL_CATEGORIES].map(c => {
          const s = selectedCat===c.id;
          return (
            <TouchableOpacity key={c.id} onPress={() => setSelectedCat(c.id)}
              style={{ flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:11, paddingVertical:6,
                       borderRadius:18, borderWidth:1.5, borderColor:s?c.color:ML.line,
                       backgroundColor:s?c.color+'18':'#fff' }}>
              <MaterialCommunityIcons name={c.icon} size={12} color={s?c.color:ML.sub} />
              <Text style={{ fontSize:12, fontWeight:s?'700':'500', color:s?c.color:ML.sub }}>{c.name}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* create button */}
      <TouchableOpacity onPress={() => { setCreateStep(1); setShowCreate(true); }}
        style={{ marginBottom:16, borderRadius:12, overflow:'hidden' }}>
        <LinearGradient colors={[ML.primary,ML.secondary]} start={{x:0,y:0}} end={{x:1,y:0}}
          style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, paddingVertical:14 }}>
          <MaterialCommunityIcons name="plus-circle" size={20} color="#fff" />
          <Text style={{ color:'#fff', fontSize:15, fontWeight:'700' }}>Create New Model</Text>
        </LinearGradient>
      </TouchableOpacity>

      {/* model cards */}
      {filteredModels.length === 0
        ? <EmptyState icon="brain" title="No models found" sub="Try adjusting your filters or create a new model" />
        : filteredModels.map(m => renderModelCard(m))
      }
    </View>
  );

  const renderModelCard = (m) => {
    const st = MODEL_STATUSES[m.status] || MODEL_STATUSES.draft;
    const typ = MODEL_TYPES.find(t=>t.id===m.type);
    const cat = MODEL_CATEGORIES.find(c=>c.id===m.category);
    const isReg = ['regression','forecasting'].includes(m.type);
    return (
      <TouchableOpacity key={m.id} style={S.card} onPress={() => openDetail(m)} activeOpacity={0.85}>
        <View style={{ height:4, backgroundColor:m.color, borderTopLeftRadius:16, borderTopRightRadius:16 }} />
        <View style={{ padding:16 }}>
          {/* header */}
          <View style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:10 }}>
            <View style={{ width:48, height:48, borderRadius:24, backgroundColor:m.color+'20',
                           alignItems:'center', justifyContent:'center' }}>
              <MaterialCommunityIcons name={m.icon} size={26} color={m.color} />
            </View>
            <View style={{ flex:1 }}>
              <Text style={{ fontSize:15, fontWeight:'800', color:ML.dark, marginBottom:5 }}>{m.name}</Text>
              <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6 }}>
                {typ && (
                  <View style={{ backgroundColor:m.color+'18', borderRadius:6, paddingHorizontal:8, paddingVertical:3,
                                 flexDirection:'row', alignItems:'center', gap:4 }}>
                    <MaterialCommunityIcons name={typ.icon} size={11} color={m.color} />
                    <Text style={{ fontSize:11, color:m.color, fontWeight:'700' }}>{typ.name}</Text>
                  </View>
                )}
                {cat && (
                  <View style={{ backgroundColor:cat.color+'15', borderRadius:6, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:11, color:cat.color, fontWeight:'600' }}>{cat.name}</Text>
                  </View>
                )}
                <View style={{ backgroundColor:st.bg, borderRadius:6, paddingHorizontal:8, paddingVertical:3,
                               flexDirection:'row', alignItems:'center', gap:4 }}>
                  <MaterialCommunityIcons name={st.icon} size={11} color={st.color} />
                  <Text style={{ fontSize:11, color:st.color, fontWeight:'700' }}>{st.label}</Text>
                </View>
              </View>
            </View>
            {m.version && (
              <View style={{ backgroundColor:'#F1F5F9', borderRadius:6, paddingHorizontal:7, paddingVertical:3 }}>
                <Text style={{ fontSize:10, color:ML.sub, fontWeight:'600' }}>v{m.version}</Text>
              </View>
            )}
          </View>

          <Text style={{ fontSize:13, color:ML.sub, lineHeight:19, marginBottom:12 }} numberOfLines={2}>
            {m.description}
          </Text>

          {/* metrics row */}
          <View style={{ flexDirection:'row', gap:10, marginBottom:12 }}>
            <View style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:10, padding:10 }}>
              <Text style={{ fontSize:9, color:ML.sub, fontWeight:'700', marginBottom:4 }}>
                <Text>{isReg ? 'R² SCORE' : 'ACCURACY'}</Text>
              </Text>
              <Text style={{ fontSize:20, fontWeight:'800',
                             color:m.accuracy>=90?ML.success:m.accuracy>=75?ML.warning:ML.danger }}>
                {isReg ? (m.r2||0).toFixed(2) : `${m.accuracy}%`}
              </Text>
              {!isReg && <AccBar value={m.accuracy} h={5} />}
            </View>
            <View style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:10, padding:10 }}>
              <Text style={{ fontSize:9, color:ML.sub, fontWeight:'700', marginBottom:4 }}>PREDICTIONS</Text>
              <Text style={{ fontSize:20, fontWeight:'800', color:ML.dark }}>{(m.predictions||0).toLocaleString()}</Text>
              <Text style={{ fontSize:10, color:ML.sub }}>avg {m.avgInferenceMs||'—'}ms</Text>
            </View>
            <View style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:10, padding:10 }}>
              <Text style={{ fontSize:9, color:ML.sub, fontWeight:'700', marginBottom:4 }}>LAST TRAINED</Text>
              <Text style={{ fontSize:12, fontWeight:'700', color:ML.dark }}>
                {m.lastTrained ? fmtDate(m.lastTrained,{day:'numeric',month:'short'}) : 'Never'}
              </Text>
              <Text style={{ fontSize:10, color:ML.sub }}>{m.retrainSchedule||'manual'}</Text>
            </View>
          </View>

          {/* drift bar */}
          {m.driftScore != null && (
            <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:12 }}>
              <Text style={{ fontSize:11, color:ML.sub }}>Drift:</Text>
              <View style={{ flex:1, height:5, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden' }}>
                <View style={{ width:`${m.driftScore*100}%`, height:'100%', borderRadius:3,
                  backgroundColor:m.driftScore<0.05?ML.success:m.driftScore<0.1?ML.warning:ML.danger }} />
              </View>
              <Text style={{ fontSize:11, fontWeight:'700',
                color:m.driftScore<0.05?ML.success:m.driftScore<0.1?ML.warning:ML.danger }}>
                {(m.driftScore*100).toFixed(1)}%
              </Text>
            </View>
          )}

          {/* actions */}
          <View style={{ flexDirection:'row', gap:8 }}>
            <TouchableOpacity onPress={() => openPredict(m)}
              style={[S.cardBtn, { flex:1, borderColor:m.color }]}>
              <MaterialCommunityIcons name="play" size={15} color={m.color} />
              <Text style={{ fontSize:13, fontWeight:'700', color:m.color }}>Predict</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => openTrain(m)}
              style={[S.cardBtn, { flex:1, borderColor:ML.line }]}>
              <MaterialCommunityIcons name="refresh" size={15} color={ML.sub} />
              <Text style={{ fontSize:13, fontWeight:'700', color:ML.sub }}>Retrain</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => doShareModel(m)} style={[S.cardBtn, { paddingHorizontal:12 }]}>
              <Ionicons name="share-outline" size={16} color={ML.sub} />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ── TRAINING TAB ──────────────────────────────────────────────────────────
  const renderTraining = () => (
    <View style={{ padding:16 }}>
      <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark, marginBottom:14 }}>
        Training History ({trainingHistory.length} runs)
      </Text>
      {trainingHistory.length === 0
        ? <EmptyState icon="school" title="No training runs" sub="Start training a model to see history" />
        : trainingHistory.map(t => {
            const m = mlModels.find(m2=>String(m2.id)===String(t.model_id));
            const inProg = t.status==='in_progress';
            return (
              <View key={t.id} style={[S.card, { overflow:'hidden' }]}>
                <View style={{ height:4, backgroundColor:inProg?ML.warning:t.status==='completed'?ML.success:ML.danger }} />
                <View style={{ padding:14 }}>
                  <View style={{ flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:10, flex:1 }}>
                      <View style={{ width:36, height:36, borderRadius:18, backgroundColor:`${m?.color||ML.primary}20`,
                                     alignItems:'center', justifyContent:'center' }}>
                        <MaterialCommunityIcons name={m?.icon||'brain'} size={20} color={m?.color||ML.primary} />
                      </View>
                      <View style={{ flex:1 }}>
                        <Text style={{ fontSize:14, fontWeight:'700', color:ML.dark }}>{m?.name||'Unknown Model'}</Text>
                        <Text style={{ fontSize:12, color:ML.sub }}>{fmtDateTime(t.date)} · {t.triggered_by||'manual'}</Text>
                      </View>
                    </View>
                    <View style={{ backgroundColor:inProg?ML.warning+'18':t.status==='completed'?ML.success+'18':ML.danger+'18',
                                   borderRadius:8, paddingHorizontal:10, paddingVertical:4 }}>
                      <Text style={{ fontSize:11, fontWeight:'700',
                        color:inProg?ML.warning:t.status==='completed'?ML.success:ML.danger }}>
                        <Text>{inProg ? 'In Progress' : t.status==='completed' ? 'Completed' : 'Failed'}</Text>
                      </Text>
                    </View>
                  </View>

                  {inProg && t.progress != null && (
                    <View style={{ marginBottom:10 }}>
                      <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                        <Text style={{ fontSize:12, color:ML.sub }}>Progress</Text>
                        <Text style={{ fontSize:12, fontWeight:'700', color:ML.warning }}>{t.progress}%</Text>
                      </View>
                      <View style={{ height:8, backgroundColor:'#E2E8F0', borderRadius:4, overflow:'hidden' }}>
                        <View style={{ width:`${t.progress}%`, height:'100%', backgroundColor:ML.warning, borderRadius:4 }} />
                      </View>
                    </View>
                  )}

                  <View style={{ flexDirection:'row', gap:8, marginBottom:8 }}>
                    {[
                      { label:'Accuracy', value:t.accuracy?`${t.accuracy}%`:'—', color:ML.success },
                      { label:'Val Acc',  value:t.valAccuracy?`${t.valAccuracy}%`:'—', color:ML.info },
                      { label:'Samples',  value:t.samples?.toLocaleString()||'—', color:ML.dark },
                      { label:'Duration', value:t.duration||'—', color:ML.dark },
                    ].map((k,i) => (
                      <View key={i} style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:8, padding:8, alignItems:'center' }}>
                        <Text style={{ fontSize:13, fontWeight:'800', color:k.color }}>{k.value}</Text>
                        <Text style={{ fontSize:10, color:ML.sub, marginTop:2 }}>{k.label}</Text>
                      </View>
                    ))}
                  </View>
                  {t.improvement && (
                    <Text style={{ fontSize:12, color:ML.success, fontWeight:'600' }}>📈 {t.improvement}</Text>
                  )}
                  {t.notes && (
                    <View style={{ backgroundColor:'#F1F5F9', borderRadius:8, padding:10, marginTop:8 }}>
                      <Text style={{ fontSize:12, color:ML.sub }}>{t.notes}</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })
      }
    </View>
  );

  // ── PREDICTIONS TAB ───────────────────────────────────────────────────────
  const renderPredictions = () => (
    <View style={{ padding:16 }}>
      <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark, marginBottom:12 }}>Recent Predictions</Text>
      {/* summary */}
      <View style={{ flexDirection:'row', gap:8, marginBottom:16 }}>
        {[
          { label:'Total',    value:predictions.length,                                  color:ML.dark    },
          { label:'Success',  value:predictions.filter(p=>p.status!=='error').length,     color:ML.success },
          { label:'Errors',   value:predictions.filter(p=>p.status==='error').length,     color:ML.danger  },
          { label:'Avg Conf', value:`${predictions.filter(p=>p.confidence).length
            ? Math.round(predictions.filter(p=>p.confidence).reduce((s,p)=>s+(p.confidence||0),0)/predictions.filter(p=>p.confidence).length)
            : 0}%`, color:ML.info },
        ].map((s,i) => (
          <View key={i} style={{ flex:1, backgroundColor:'#fff', borderRadius:10, padding:10,
                                  alignItems:'center', borderWidth:1, borderColor:ML.line }}>
            <Text style={{ fontSize:18, fontWeight:'800', color:s.color }}>{s.value}</Text>
            <Text style={{ fontSize:10, color:ML.sub, marginTop:2 }}>{s.label}</Text>
          </View>
        ))}
      </View>

      {predictions.length === 0
        ? <EmptyState icon="chart-line" title="No predictions yet" sub="Run a prediction to see results here" />
        : predictions.map(pred => {
            const model = mlModels.find(m=>String(m.id)===String(pred.model_id));
            const conf  = pred.confidence||0;
            const cc    = conf>=85?ML.success:conf>=65?ML.warning:ML.danger;
            const isErr = pred.status==='error';
            return (
              <View key={pred.id} style={[S.card, isErr&&{borderLeftWidth:3,borderLeftColor:ML.danger}]}>
                <View style={{ padding:16 }}>
                  <View style={{ flexDirection:'row', alignItems:'flex-start', gap:10, marginBottom:10 }}>
                    <View style={{ width:36, height:36, borderRadius:18, backgroundColor:`${model?.color||ML.sub}18`,
                                   alignItems:'center', justifyContent:'center' }}>
                      <MaterialCommunityIcons name={model?.icon||'brain'} size={18} color={model?.color||ML.sub} />
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:14, fontWeight:'700', color:ML.dark }}>{model?.name||'Unknown Model'}</Text>
                      <Text style={{ fontSize:12, color:ML.sub }}>{fmtDateTime(pred.date)}
                        {pred.responseMs ? ` · ${pred.responseMs}ms` : ''}</Text>
                    </View>
                    {!isErr && (
                      <View style={{ alignItems:'center' }}>
                        <View style={{ width:46, height:46, borderRadius:23, borderWidth:3, borderColor:cc,
                                       alignItems:'center', justifyContent:'center' }}>
                          <Text style={{ fontSize:12, fontWeight:'800', color:cc }}>{conf}%</Text>
                        </View>
                        <Text style={{ fontSize:9, color:ML.sub, marginTop:2 }}>CONF</Text>
                      </View>
                    )}
                    {isErr && (
                      <View style={{ backgroundColor:ML.danger+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:4 }}>
                        <Text style={{ fontSize:11, color:ML.danger, fontWeight:'700' }}>Error</Text>
                      </View>
                    )}
                  </View>
                  <View style={{ backgroundColor:'#F8FAFC', borderRadius:8, padding:10, marginBottom:8 }}>
                    <Text style={{ fontSize:11, color:ML.sub, fontWeight:'700', marginBottom:3 }}>INPUT</Text>
                    <Text style={{ fontSize:12, color:ML.dark,
                                   fontFamily:Platform.OS==='ios'?'Menlo':'monospace' }} numberOfLines={2}>
                      {pred.input_data||'—'}
                    </Text>
                  </View>
                  <View style={{ backgroundColor:isErr?ML.danger+'10':ML.success+'10', borderRadius:8, padding:10 }}>
                    <Text style={{ fontSize:11, color:isErr?ML.danger:ML.success, fontWeight:'700', marginBottom:3 }}>RESULT</Text>
                    <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>{pred.result||'—'}</Text>
                  </View>
                  {!isErr && (
                    <View style={{ height:5, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden', marginTop:8 }}>
                      <View style={{ width:`${conf}%`, height:'100%', backgroundColor:cc, borderRadius:3 }} />
                    </View>
                  )}
                </View>
              </View>
            );
          })
      }
    </View>
  );

  // ── DATASETS TAB ──────────────────────────────────────────────────────────
  const renderDatasets = () => (
    <View style={{ padding:16 }}>
      <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark, marginBottom:12 }}>Training Datasets</Text>
      <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
        {[
          { label:'Datasets',   value:datasets.length + (innoDataset?1:0),              color:ML.primary },
          { label:'Ready',      value:datasets.filter(d=>d.status==='ready').length + (innoDataset?1:0), color:ML.success },
          { label:'Total Rows', value:`${((datasets.reduce((s,d)=>s+(d.size||0),0)+(linkedInnovations.length))/1000).toFixed(0)}k`, color:ML.info },
        ].map((s,i) => (
          <View key={i} style={{ flex:1, backgroundColor:'#fff', borderRadius:12, padding:14,
                                  alignItems:'center', borderWidth:1, borderColor:ML.line }}>
            <Text style={{ fontSize:22, fontWeight:'800', color:s.color }}>{s.value}</Text>
            <Text style={{ fontSize:11, color:ML.sub, marginTop:3 }}>{s.label}</Text>
          </View>
        ))}
      </View>

      {/* ── Innovation Hub live dataset ── */}
      {innoDataset && (
        <TouchableOpacity style={[S.card, { borderWidth:2, borderColor:'#fdb91360' }]}
          onPress={() => { setSelDataset(innoDataset); setShowDataset(true); }}>
          <View style={{ height:4, backgroundColor:'#fdb913', borderTopLeftRadius:16, borderTopRightRadius:16 }} />
          <View style={{ padding:16 }}>
            <View style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:10 }}>
              <View style={{ width:44, height:44, borderRadius:22, backgroundColor:'#fdb91318',
                             alignItems:'center', justifyContent:'center' }}>
                <Ionicons name="bulb-outline" size={24} color="#fdb913" />
              </View>
              <View style={{ flex:1 }}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:4 }}>
                  <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark }}>Innovation Hub Ideas</Text>
                  <View style={{ backgroundColor:'#fdb91318', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                    <Text style={{ fontSize:10, fontWeight:'800', color:'#b8860b' }}>LIVE</Text>
                  </View>
                </View>
                <Text style={{ fontSize:12, color:ML.sub }}>{innoDataset.description}</Text>
              </View>
              <View style={{ backgroundColor:ML.success+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:4 }}>
                <Text style={{ fontSize:11, fontWeight:'700', color:ML.success }}>ready</Text>
              </View>
            </View>
            <View style={{ flexDirection:'row', gap:8, marginBottom:12 }}>
              {[
                { label:'Ideas',    value:linkedInnovations.length },
                { label:'Features', value:'8'                      },
                { label:'Format',   value:'JSON'                   },
                { label:'Source',   value:'Internal'               },
              ].map((k,i) => (
                <View key={i} style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:8, padding:8, alignItems:'center' }}>
                  <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>{k.value}</Text>
                  <Text style={{ fontSize:10, color:ML.sub, marginTop:2 }}>{k.label}</Text>
                </View>
              ))}
            </View>
            {/* Innovation mini-list */}
            {linkedInnovations.slice(0,3).map((inn,i) => (
              <TouchableOpacity key={inn.id||i} onPress={() => sendInnovationToPrediction(inn)}
                style={{ flexDirection:'row', alignItems:'center', gap:8, paddingVertical:7,
                         borderTopWidth:i===0?1:0, borderTopColor:'#F1F5F9' }}>
                <Ionicons name="bulb-outline" size={14} color="#fdb913" />
                <Text style={{ flex:1, fontSize:12, color:ML.dark, fontWeight:'600' }} numberOfLines={1}>
                  {inn.title}
                </Text>
                <View style={{ backgroundColor:'#fdb91318', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                  <Text style={{ fontSize:10, color:'#b8860b', fontWeight:'700' }}>
                    {inn.impact?.toUpperCase()||'MED'}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => sendInnovationToPrediction(inn)}
                  style={{ backgroundColor:ML.primary+'18', borderRadius:6, paddingHorizontal:8, paddingVertical:4 }}>
                  <Text style={{ fontSize:11, fontWeight:'700', color:ML.primary }}>Predict</Text>
                </TouchableOpacity>
              </TouchableOpacity>
            ))}
            {linkedInnovations.length > 3 && (
              <Text style={{ fontSize:12, color:ML.sub, marginTop:6 }}>
                +{linkedInnovations.length-3} more ideas in Innovation Hub
              </Text>
            )}
            <View style={{ flexDirection:'row', gap:8, marginTop:12 }}>
              <TouchableOpacity onPress={() => navigation?.navigate('InnovativeScreen')}
                style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6,
                         paddingVertical:10, borderRadius:10, borderWidth:1.5, borderColor:'#fdb913' }}>
                <Ionicons name="bulb-outline" size={15} color="#fdb913" />
                <Text style={{ fontSize:13, fontWeight:'700', color:'#fdb913' }}>Open Innovation Hub</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => openTrain(mlModels[0] || { id:'new', name:'Job Match Predictor', type:'classification', icon:'brain', color:'#6366F1' })}
                style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6,
                         paddingVertical:10, borderRadius:10, backgroundColor:ML.primary }}>
                <MaterialCommunityIcons name="school" size={15} color="#fff" />
                <Text style={{ fontSize:13, fontWeight:'700', color:'#fff' }}>Train on Ideas</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      )}

      {datasets.length === 0 && !innoDataset
        ? <EmptyState icon="database" title="No datasets" sub="Upload data to begin training" />
        : datasets.map(ds => {
            const qc = ds.quality>=90?ML.success:ds.quality>=75?ML.warning:ML.danger;
            return (
              <TouchableOpacity key={ds.id} style={S.card}
                onPress={() => { setSelDataset(ds); setShowDataset(true); }}>
                <View style={{ padding:16 }}>
                  <View style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:10 }}>
                    <View style={{ width:40, height:40, borderRadius:20, backgroundColor:ML.primary+'18',
                                   alignItems:'center', justifyContent:'center' }}>
                      <MaterialCommunityIcons name={
                        ds.type==='text'?'text-box':ds.type==='timeseries'?'chart-timeline':'table'} size={22} color={ML.primary} />
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:14, fontWeight:'700', color:ML.dark }}>{ds.name}</Text>
                      <Text style={{ fontSize:12, color:ML.sub, marginTop:2 }}>{ds.description}</Text>
                    </View>
                    <View style={{ backgroundColor:ds.status==='ready'?ML.success+'18':ML.warning+'18',
                                   borderRadius:8, paddingHorizontal:8, paddingVertical:4 }}>
                      <Text style={{ fontSize:11, fontWeight:'700', color:ds.status==='ready'?ML.success:ML.warning }}>
                        {ds.status}
                      </Text>
                    </View>
                  </View>
                  <View style={{ flexDirection:'row', gap:8, marginBottom:10 }}>
                    {[
                      { label:'Rows',     value:(ds.size||0).toLocaleString() },
                      { label:'Features', value:String(ds.features||'—')     },
                      { label:'Format',   value:ds.format||'—'               },
                      { label:'Source',   value:ds.source||'—'               },
                    ].map((k,i) => (
                      <View key={i} style={{ flex:1, backgroundColor:'#F8FAFC', borderRadius:8, padding:8, alignItems:'center' }}>
                        <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>{k.value}</Text>
                        <Text style={{ fontSize:10, color:ML.sub, marginTop:2 }}>{k.label}</Text>
                      </View>
                    ))}
                  </View>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                    <Text style={{ fontSize:11, color:ML.sub }}>Data Quality</Text>
                    <Text style={{ fontSize:11, fontWeight:'800', color:qc }}>{ds.quality}%</Text>
                  </View>
                  <View style={{ height:6, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden' }}>
                    <View style={{ width:`${ds.quality}%`, height:'100%', backgroundColor:qc, borderRadius:3 }} />
                  </View>
                  {ds.lastUpdated && (
                    <Text style={{ fontSize:11, color:ML.sub, marginTop:8 }}>
                      Updated: {fmtDate(ds.lastUpdated)}
                      <Text>{ds.linkedModels?.length ? ` · ${ds.linkedModels.length} linked model${ds.linkedModels.length>1?'s':''}` : ''}</Text>
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          })
      }
    </View>
  );

  // ── ANALYTICS TAB ─────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const active = mlModels.filter(m=>m.status==='active'&&m.accuracy>0);
    const top    = [...mlModels].sort((a,b)=>(b.accuracy||0)-(a.accuracy||0))[0];
    return (
      <View style={{ padding:16 }}>
        <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark, marginBottom:14 }}>ML Analytics</Text>
        {/* KPIs */}
        <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
          {[
            { icon:'trending-up', label:'Avg Accuracy',
              value: active.length ? `${(active.reduce((s,m)=>s+m.accuracy,0)/active.length).toFixed(1)}%` : '—',
              color:ML.success },
            { icon:'chart-line', label:'Total Predictions',
              value: mlModels.reduce((s,m)=>s+(m.predictions||0),0).toLocaleString(),
              color:ML.primary },
          ].map((k,i) => (
            <View key={i} style={{ flex:1, backgroundColor:'#fff', borderRadius:14, padding:16,
                                    borderWidth:1, borderColor:ML.line }}>
              <MaterialCommunityIcons name={k.icon} size={22} color={k.color} style={{ marginBottom:8 }} />
              <Text style={{ fontSize:22, fontWeight:'800', color:k.color }}>{k.value}</Text>
              <Text style={{ fontSize:12, color:ML.sub, marginTop:4 }}>{k.label}</Text>
            </View>
          ))}
        </View>

        {/* Performance comparison */}
        <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:16,
                       borderWidth:1, borderColor:ML.line }}>
          <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:14 }}>
            Model Performance Ranking
          </Text>
          {[...mlModels].filter(m=>m.accuracy>0).sort((a,b)=>b.accuracy-a.accuracy).map((m,i) => (
            <View key={m.id} style={{ marginBottom:14 }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
                  <Text style={{ fontSize:13, color:ML.sub, fontWeight:'700', minWidth:20 }}>#{i+1}</Text>
                  <MaterialCommunityIcons name={m.icon} size={14} color={m.color} />
                  <Text style={{ fontSize:13, fontWeight:'600', color:ML.dark, maxWidth:width*0.45 }} numberOfLines={1}>
                    {m.name}
                  </Text>
                </View>
                <Text style={{ fontSize:14, fontWeight:'800',
                               color:m.accuracy>=90?ML.success:m.accuracy>=75?ML.warning:ML.danger }}>
                  {m.accuracy}%
                </Text>
              </View>
              <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
                <View style={{ flex:1, height:10, backgroundColor:'#F1F5F9', borderRadius:5, overflow:'hidden' }}>
                  <View style={{ width:`${m.accuracy}%`, height:'100%', backgroundColor:m.color, borderRadius:5 }} />
                </View>
                <Text style={{ fontSize:10, color:ML.sub, minWidth:50 }}>{(m.predictions||0).toLocaleString()} preds</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Type distribution */}
        <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:16,
                       borderWidth:1, borderColor:ML.line }}>
          <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:14 }}>Models by Type</Text>
          {MODEL_TYPES.map(t => {
            const count = mlModels.filter(m=>m.type===t.id).length;
            if (!count) return null;
            return (
              <View key={t.id} style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:10 }}>
                <MaterialCommunityIcons name={t.icon} size={15} color={t.color} />
                <Text style={{ fontSize:13, color:ML.dark, flex:1 }}>{t.name}</Text>
                <View style={{ width:100, height:8, backgroundColor:'#F1F5F9', borderRadius:4, overflow:'hidden' }}>
                  <View style={{ width:`${(count/mlModels.length)*100}%`, height:'100%', backgroundColor:t.color, borderRadius:4 }} />
                </View>
                <Text style={{ fontSize:12, fontWeight:'700', color:t.color, minWidth:16, textAlign:'right' }}>{count}</Text>
              </View>
            );
          })}
        </View>

        {/* Innovation Hub cross-screen stats */}
        {linkedInnovations.length > 0 && (
          <TouchableOpacity onPress={() => navigation?.navigate('InnovativeScreen')}
            style={{ backgroundColor:'#fdb91315', borderRadius:14, padding:16, marginBottom:16,
                     borderWidth:1.5, borderColor:'#fdb91330' }}>
            <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:12 }}>
              <Ionicons name="bulb-outline" size={20} color="#fdb913" />
              <Text style={{ fontSize:14, fontWeight:'800', color:'#b8860b' }}>Innovation Hub</Text>
              <View style={{ backgroundColor:'#fdb91318', borderRadius:6, paddingHorizontal:7, paddingVertical:2, marginLeft:'auto' }}>
                <Text style={{ fontSize:10, fontWeight:'800', color:'#b8860b' }}>LIVE LINK</Text>
              </View>
            </View>
            <View style={{ flexDirection:'row', gap:10 }}>
              {[
                { label:'Linked Ideas',   value:String(linkedInnovations.length), color:'#b8860b' },
                { label:'High Impact',    value:String(linkedInnovations.filter(i=>i.impact==='high'||i.impact==='critical').length), color:ML.danger },
                { label:'AI/ML Type',     value:String(linkedInnovations.filter(i=>i.type==='ai_ml').length), color:ML.success },
              ].map((s,i) => (
                <View key={i} style={{ flex:1, backgroundColor:'rgba(255,255,255,0.6)', borderRadius:10, padding:10, alignItems:'center' }}>
                  <Text style={{ fontSize:18, fontWeight:'800', color:s.color }}>{s.value}</Text>
                  <Text style={{ fontSize:10, color:ML.sub, marginTop:2, textAlign:'center' }}>{s.label}</Text>
                </View>
              ))}
            </View>
            <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, marginTop:10 }}>
              <Text style={{ fontSize:13, fontWeight:'700', color:'#b8860b' }}>Open Innovation Hub</Text>
              <Ionicons name="arrow-forward" size={14} color="#b8860b" />
            </View>
          </TouchableOpacity>
        )}
          <View style={{ backgroundColor:top.color+'15', borderRadius:14, padding:16,
                         borderWidth:1, borderColor:top.color+'30' }}>
            <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:10 }}>
              <MaterialCommunityIcons name="trophy" size={20} color={top.color} />
              <Text style={{ fontSize:14, fontWeight:'800', color:top.color }}>Top Performer</Text>
            </View>
            <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark, marginBottom:8 }}>{top.name}</Text>
            <View style={{ flexDirection:'row', gap:10 }}>
              {[
                { label:'Accuracy',    value:`${top.accuracy}%` },
                { label:'Predictions', value:(top.predictions||0).toLocaleString() },
                { label:'Version',     value:`v${top.version||'1.0.0'}` },
              ].map((s,i) => (
                <View key={i} style={{ flex:1, alignItems:'center', backgroundColor:'rgba(255,255,255,0.55)',
                                       borderRadius:8, padding:8 }}>
                  <Text style={{ fontSize:15, fontWeight:'800', color:ML.dark }}>{s.value}</Text>
                  <Text style={{ fontSize:10, color:ML.sub, marginTop:2 }}>{s.label}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
      </View>
    );
  };

  // ── MONITORING TAB ────────────────────────────────────────────────────────
  const renderMonitoring = () => (
    <View style={{ padding:16 }}>
      <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark }}>ML Monitoring</Text>
        <View style={{ backgroundColor:unresolved.length>0?ML.danger+'18':ML.success+'18',
                       borderRadius:8, paddingHorizontal:10, paddingVertical:4 }}>
          <Text style={{ fontSize:12, fontWeight:'700', color:unresolved.length>0?ML.danger:ML.success }}>
            {unresolved.length} active alerts
          </Text>
        </View>
      </View>

      {/* Drift monitor */}
      <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:16,
                     borderWidth:1, borderColor:ML.line }}>
        <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:14 }}>Data Drift Monitor</Text>
        {mlModels.filter(m=>m.driftScore!=null).map(m => {
          const dc = m.driftScore<0.05?ML.success:m.driftScore<0.1?ML.warning:ML.danger;
          return (
            <View key={m.id} style={{ marginBottom:12 }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                <Text style={{ fontSize:13, color:ML.dark, flex:1 }} numberOfLines={1}>{m.name}</Text>
                <Text style={{ fontSize:12, fontWeight:'800', color:dc }}>
                  {(m.driftScore*100).toFixed(1)}%{m.driftScore>=0.1?' ⚠️':m.driftScore<0.05?' ✅':''}
                </Text>
              </View>
              <View style={{ height:8, backgroundColor:'#F1F5F9', borderRadius:4, overflow:'hidden' }}>
                <View style={{ width:`${Math.min(m.driftScore*500,100)}%`, height:'100%',
                  backgroundColor:dc, borderRadius:4 }} />
              </View>
            </View>
          );
        })}
        <Text style={{ fontSize:11, color:ML.sub, marginTop:4 }}>
          &lt;5% = healthy · 5–10% = watch · &gt;10% = retrain recommended
        </Text>
      </View>

      {/* Alerts */}
      <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:10 }}>Alerts</Text>
      {alerts.length === 0
        ? <EmptyState icon="bell-outline" title="No alerts" sub="All models operating normally" />
        : alerts.map(alert => {
            const model = mlModels.find(m=>String(m.id)===String(alert.model_id));
            const sc    = alert.severity==='high'?ML.danger:alert.severity==='medium'?ML.warning:ML.info;
            return (
              <View key={alert.id} style={[S.card, alert.resolved&&{opacity:0.55}]}>
                <View style={{ padding:14, flexDirection:'row', gap:10 }}>
                  <View style={{ width:36, height:36, borderRadius:18, backgroundColor:sc+'18',
                                 alignItems:'center', justifyContent:'center' }}>
                    <Ionicons name={
                      alert.type==='drift'?'analytics-outline':
                      alert.type==='performance'?'trending-down-outline':'information-circle-outline'
                    } size={18} color={sc} />
                  </View>
                  <View style={{ flex:1 }}>
                    <View style={{ flexDirection:'row', gap:6, flexWrap:'wrap', marginBottom:4 }}>
                      <View style={{ backgroundColor:sc+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                        <Text style={{ fontSize:10, fontWeight:'800', color:sc }}>{String(alert.severity ?? '').toUpperCase()}</Text>
                      </View>
                      <View style={{ backgroundColor:'#F1F5F9', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                        <Text style={{ fontSize:10, fontWeight:'600', color:ML.sub }}>{alert.type.replace('_',' ')}</Text>
                      </View>
                      {alert.resolved && (
                        <View style={{ backgroundColor:ML.success+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                          <Text style={{ fontSize:10, fontWeight:'800', color:ML.success }}>RESOLVED</Text>
                        </View>
                      )}
                    </View>
                    <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark, marginBottom:3 }}>
                      {model?.name||'Unknown Model'}
                    </Text>
                    <Text style={{ fontSize:12, color:ML.sub, lineHeight:17 }}>{alert.message}</Text>
                    <Text style={{ fontSize:11, color:ML.sub, marginTop:4 }}>{fmtDateTime(alert.timestamp)}</Text>
                    {!alert.resolved && (
                      <View style={{ flexDirection:'row', gap:8, marginTop:10 }}>
                        <TouchableOpacity onPress={() => resolveAlert(alert.id)}
                          style={{ backgroundColor:ML.success+'18', borderRadius:8, paddingHorizontal:12, paddingVertical:6 }}>
                          <Text style={{ fontSize:12, fontWeight:'700', color:ML.success }}>Resolve</Text>
                        </TouchableOpacity>
                        {alert.type==='drift' && (
                          <TouchableOpacity onPress={() => {
                              const m = mlModels.find(m=>String(m.id)===String(alert.model_id));
                              if (m) openTrain(m);
                            }}
                            style={{ backgroundColor:ML.warning+'18', borderRadius:8, paddingHorizontal:12, paddingVertical:6 }}>
                            <Text style={{ fontSize:12, fontWeight:'700', color:ML.warning }}>Retrain Now</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                </View>
              </View>
            );
          })
      }

      {/* Uptime */}
      <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginTop:4,
                     borderWidth:1, borderColor:ML.line }}>
        <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:14 }}>Model Uptime</Text>
        {mlModels.map(m => {
          const pct = m.status==='active' ? 99.5+Math.random()*0.4 : m.status==='training' ? 100 : 0;
          return (
            <View key={m.id} style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:10 }}>
              <View style={{ width:8, height:8, borderRadius:4, backgroundColor:
                m.status==='active'?ML.success:m.status==='training'?ML.warning:ML.danger }} />
              <Text style={{ fontSize:13, color:ML.dark, flex:1 }} numberOfLines={1}>{m.name}</Text>
              <Text style={{ fontSize:12, fontWeight:'700',
                color:m.status==='active'?ML.success:m.status==='training'?ML.warning:ML.danger }}>
                <Text>{m.status==='active'?`${pct.toFixed(2)}%`:m.status==='training'?'Training…':'Offline'}</Text>
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );

  // ── MODEL DETAILS MODAL ───────────────────────────────────────────────────
  const renderDetailsModal = () => {
    if (!selectedModel) return null;
    const m = selectedModel;
    const st = MODEL_STATUSES[m.status]||MODEL_STATUSES.draft;
    const isReg = ['regression','forecasting'].includes(m.type);
    const DTABS = ['overview','metrics','history','monitoring'];
    return (
      <Modal visible={showDetails} animationType="slide" transparent onRequestClose={() => setShowDetails(false)}>
        <View style={S.overlay}>
          <View style={S.sheet}>
            <LinearGradient colors={[m.color, m.color+'bb']} style={S.sheetHdr}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
                <View style={{ flex:1, marginRight:12 }}>
                  <Text style={{ color:'#fff', fontSize:18, fontWeight:'800', marginBottom:3 }}>{m.name}</Text>
                  <Text style={{ color:'rgba(255,255,255,0.8)', fontSize:12 }}>
                    v{m.version||'1.0.0'} · {MODEL_CATEGORIES.find(c=>c.id===m.category)?.name||m.category}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setShowDetails(false)}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
              <View style={{ flexDirection:'row', gap:8, marginTop:12 }}>
                {[
                  { label:'Accuracy',    value:isReg?`R²=${(m.r2||0).toFixed(2)}`:`${m.accuracy}%` },
                  { label:'Predictions', value:(m.predictions||0).toLocaleString() },
                  { label:'Status',      value:st.label },
                  { label:'Drift',       value:`${((m.driftScore||0)*100).toFixed(1)}%` },
                ].map((k,i) => (
                  <View key={i} style={{ flex:1, backgroundColor:'rgba(0,0,0,0.2)', borderRadius:10, padding:8, alignItems:'center' }}>
                    <Text style={{ fontSize:13, fontWeight:'800', color:'#fff' }}>{k.value}</Text>
                    <Text style={{ fontSize:9, color:'rgba(255,255,255,0.7)', marginTop:2 }}>{k.label}</Text>
                  </View>
                ))}
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop:12 }}
                contentContainerStyle={{ gap:8 }}>
                {DTABS.map(t => (
                  <TouchableOpacity key={t} onPress={() => setDetailTab(t)}
                    style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:20,
                             backgroundColor:detailTab===t?'#fff':'rgba(255,255,255,0.2)' }}>
                    <Text style={{ fontSize:12, fontWeight:'700', textTransform:'capitalize',
                                   color:detailTab===t?m.color:'rgba(255,255,255,0.9)' }}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </LinearGradient>

            <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:20, paddingBottom:40 }}>
              {/* OVERVIEW */}
              {detailTab==='overview' && (
                <View>
                  <View style={{ backgroundColor:'#F8FAFC', borderRadius:12, padding:14, marginBottom:16 }}>
                    <Text style={{ fontSize:13, color:ML.sub, lineHeight:20 }}>{m.description}</Text>
                  </View>
                  <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:10 }}>Input Features</Text>
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:16 }}>
                    {(m.features||[]).map((f,i) => (
                      <View key={i} style={{ backgroundColor:m.color+'15', borderRadius:8, paddingHorizontal:10, paddingVertical:6 }}>
                        <Text style={{ fontSize:12, color:m.color, fontWeight:'600' }}>{f}</Text>
                      </View>
                    ))}
                    {!(m.features||[]).length && <Text style={{ color:ML.sub }}>No features defined</Text>}
                  </View>
                  <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:8 }}>Configuration</Text>
                  <InfoRow icon="cog"               label="Optimizer"       value={m.hyperparams?.optimizer||'Adam'} />
                  <InfoRow icon="counter"           label="Epochs"          value={String(m.hyperparams?.epochs||100)} />
                  <InfoRow icon="format-list-group" label="Batch Size"      value={String(m.hyperparams?.batchSize||32)} />
                  <InfoRow icon="percent"           label="Dropout"         value={`${((m.hyperparams?.dropout||0.2)*100).toFixed(0)}%`} />
                  <InfoRow icon="lightning-bolt"    label="Learning Rate"   value={String(m.hyperparams?.learningRate||0.001)} />
                  <InfoRow icon="calendar-clock"    label="Retrain"         value={m.retrainSchedule||'manual'} />
                  <InfoRow icon="web"               label="API Endpoint"    value={m.apiEndpoint||'—'} />
                  <InfoRow icon="timer-outline"     label="Avg Inference"   value={`${m.avgInferenceMs||'—'}ms`} />
                  <InfoRow icon="database"          label="Training Rows"   value={(m.dataSize||0).toLocaleString()} color={ML.info} />
                  {m.deployedAt && <InfoRow icon="rocket-launch" label="Deployed" value={fmtDate(m.deployedAt)} color={ML.success} />}
                </View>
              )}

              {/* METRICS */}
              {detailTab==='metrics' && (
                <View>
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:16 }}>
                    {[
                      { label:'Accuracy',  value:m.accuracy?`${m.accuracy}%`:'—',          show:true    },
                      { label:'Precision', value:m.precision?`${m.precision}%`:'—',         show:!isReg  },
                      { label:'Recall',    value:m.recall?`${m.recall}%`:'—',               show:!isReg  },
                      { label:'F1 Score',  value:m.f1?`${m.f1}%`:'—',                       show:!isReg  },
                      { label:'AUC-ROC',   value:m.auc?m.auc.toFixed(3):'—',               show:!isReg  },
                      { label:'R² Score',  value:m.r2?m.r2.toFixed(3):'—',                 show:isReg   },
                      { label:'RMSE',      value:m.rmse?`£${m.rmse.toLocaleString()}`:'—',  show:isReg   },
                      { label:'MAE',       value:m.mae?`£${m.mae.toLocaleString()}`:'—',    show:isReg   },
                    ].filter(k=>k.show).map((k,i) => (
                      <View key={i} style={{ width:(width-72)/2, backgroundColor:'#F8FAFC', borderRadius:12,
                                             padding:14, borderWidth:1, borderColor:ML.line }}>
                        <Text style={{ fontSize:11, color:ML.sub, fontWeight:'600', marginBottom:6 }}>{k.label}</Text>
                        <Text style={{ fontSize:22, fontWeight:'800', color:ML.dark }}>{k.value}</Text>
                      </View>
                    ))}
                  </View>
                  {!isReg && m.accuracy>0 && (
                    <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, borderWidth:1, borderColor:ML.line }}>
                      <Text style={{ fontSize:13, fontWeight:'800', color:ML.dark, marginBottom:14 }}>Performance Breakdown</Text>
                      {[
                        { label:'Accuracy',  value:m.accuracy,  color:ML.success },
                        { label:'Precision', value:m.precision, color:ML.info    },
                        { label:'Recall',    value:m.recall,    color:ML.warning },
                        { label:'F1 Score',  value:m.f1,        color:ML.primary },
                      ].filter(k=>k.value).map((k,i) => (
                        <View key={i} style={{ marginBottom:12 }}>
                          <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                            <Text style={{ fontSize:12, color:ML.sub }}>{k.label}</Text>
                            <Text style={{ fontSize:12, fontWeight:'700', color:k.color }}>{k.value}%</Text>
                          </View>
                          <View style={{ height:10, backgroundColor:'#F1F5F9', borderRadius:5, overflow:'hidden' }}>
                            <View style={{ width:`${k.value}%`, height:'100%', backgroundColor:k.color, borderRadius:5 }} />
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}

              {/* HISTORY */}
              {detailTab==='history' && (
                <View>
                  {trainingHistory.filter(t=>String(t.model_id)===String(m.id)).length===0
                    ? <EmptyState icon="history" title="No training history" sub="Train this model to see history" />
                    : trainingHistory.filter(t=>String(t.model_id)===String(m.id)).map(t => (
                        <View key={t.id} style={{ backgroundColor:'#F8FAFC', borderRadius:12, padding:14,
                                                   marginBottom:10, borderWidth:1, borderColor:ML.line }}>
                          <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:8 }}>
                            <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>{fmtDate(t.date)}</Text>
                            <View style={{ backgroundColor:t.status==='completed'?ML.success+'18':ML.warning+'18',
                                           borderRadius:6, paddingHorizontal:8, paddingVertical:3 }}>
                              <Text style={{ fontSize:11, fontWeight:'700', color:t.status==='completed'?ML.success:ML.warning }}>
                                {t.status}
                              </Text>
                            </View>
                          </View>
                          <View style={{ flexDirection:'row', gap:8 }}>
                            {[
                              { l:'Accuracy', v:t.accuracy?`${t.accuracy}%`:'—' },
                              { l:'Duration', v:t.duration||'—' },
                              { l:'Samples',  v:t.samples?.toLocaleString()||'—' },
                              { l:'By',       v:t.triggered_by||'—' },
                            ].map((k,i) => (
                              <View key={i} style={{ flex:1, alignItems:'center' }}>
                                <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>{k.v}</Text>
                                <Text style={{ fontSize:10, color:ML.sub }}>{k.l}</Text>
                              </View>
                            ))}
                          </View>
                          {t.improvement && (
                            <Text style={{ fontSize:12, color:ML.success, fontWeight:'600', marginTop:8 }}>
                              📈 {t.improvement}
                            </Text>
                          )}
                          {t.notes && <Text style={{ fontSize:12, color:ML.sub, marginTop:6 }}>{t.notes}</Text>}
                        </View>
                      ))
                  }
                </View>
              )}

              {/* MONITORING (detail) */}
              {detailTab==='monitoring' && (
                <View>
                  <InfoRow icon="wave"                 label="Drift Score"       value={`${((m.driftScore||0)*100).toFixed(1)}%`}
                    color={m.driftScore<0.05?ML.success:m.driftScore<0.1?ML.warning:ML.danger} />
                  <InfoRow icon="timer-outline"        label="Avg Inference"     value={`${m.avgInferenceMs||'—'}ms`} />
                  <InfoRow icon="calendar-refresh"     label="Retrain Schedule"  value={m.retrainSchedule||'manual'} />
                  <InfoRow icon="alert-decagram"       label="Alert Threshold"   value={`${m.alertThreshold||85}%`} />
                  <InfoRow icon="database-clock"       label="Training Data"     value={(m.dataSize||0).toLocaleString()+' rows'} />
                  <View style={{ height:14 }} />
                  <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:10 }}>Active Alerts</Text>
                  {alerts.filter(a=>String(a.model_id)===String(m.id)&&!a.resolved).length===0
                    ? <View style={{ backgroundColor:ML.success+'15', borderRadius:12, padding:14,
                                     flexDirection:'row', gap:10, alignItems:'center' }}>
                        <MaterialCommunityIcons name="check-circle" size={20} color={ML.success} />
                        <Text style={{ fontSize:13, color:ML.success, fontWeight:'600' }}>No active alerts</Text>
                      </View>
                    : alerts.filter(a=>String(a.model_id)===String(m.id)&&!a.resolved).map(alert => (
                        <View key={alert.id} style={{ backgroundColor:ML.danger+'10', borderRadius:12, padding:14,
                                                       marginBottom:8, borderLeftWidth:3, borderLeftColor:ML.danger }}>
                          <Text style={{ fontSize:13, fontWeight:'700', color:ML.danger, marginBottom:4 }}>
                            {String(alert.severity ?? '').toUpperCase()} · {alert.type}
                          </Text>
                          <Text style={{ fontSize:13, color:ML.sub }}>{alert.message}</Text>
                          <TouchableOpacity onPress={() => resolveAlert(alert.id)} style={{ marginTop:8 }}>
                            <Text style={{ fontSize:12, fontWeight:'700', color:ML.success }}>Mark Resolved</Text>
                          </TouchableOpacity>
                        </View>
                      ))
                  }
                </View>
              )}
            </ScrollView>

            <View style={S.mActions}>
              <TouchableOpacity onPress={() => doDeleteModel(m)}
                style={[S.mBtn, { borderColor:ML.danger, flex:0, paddingHorizontal:14 }]}>
                <MaterialCommunityIcons name="delete-outline" size={18} color={ML.danger} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => doShareModel(m)}
                style={[S.mBtn, { borderColor:ML.success, flex:0, paddingHorizontal:14 }]}>
                <Ionicons name="share-outline" size={18} color={ML.success} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => openTrain(m)} style={[S.mBtn, { flex:1 }]}>
                <MaterialCommunityIcons name="refresh" size={16} color={ML.sub} />
                <Text style={{ color:ML.sub, fontWeight:'700' }}>Retrain</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => openPredict(m)} style={[S.mBtn, S.mBtnPrimary, { flex:2 }]}>
                <MaterialCommunityIcons name="play" size={16} color="#fff" />
                <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Run Prediction</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ── TRAINING MODAL ────────────────────────────────────────────────────────
  const renderTrainModal = () => (
    <Modal visible={showTraining} animationType="slide" transparent onRequestClose={() => setShowTraining(false)}>
      <View style={S.overlay}>
        <View style={S.sheet}>
          <LinearGradient colors={[ML.primary,ML.secondary]} style={S.sheetHdr}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
                <MaterialCommunityIcons name="school" size={24} color="#fff" />
                <View>
                  <Text style={{ color:'#fff', fontSize:16, fontWeight:'800' }}>Train Model</Text>
                  <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:12 }}>{selectedModel?.name}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setShowTraining(false)}>
                <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
              </TouchableOpacity>
            </View>
          </LinearGradient>

          <ScrollView contentContainerStyle={{ padding:20, paddingBottom:30 }}>
            {trainProgress ? (
              <View style={{ alignItems:'center' }}>
                <View style={{ backgroundColor: trainProgress.status==='done'?ML.success+'12':trainProgress.status==='error'?ML.danger+'12':ML.primary+'10',
                               borderRadius:14, padding:24, alignItems:'center', width:'100%', marginBottom:20 }}>
                  {trainProgress.status==='done'
                    ? <MaterialCommunityIcons name="check-circle" size={64} color={ML.success} />
                    : trainProgress.status==='error'
                    ? <MaterialCommunityIcons name="alert-circle" size={64} color={ML.danger} />
                    : <ActivityIndicator size={64} color={ML.primary} />
                  }
                  <Text style={{ fontSize:18, fontWeight:'800', color:ML.dark, marginTop:14 }}>
                    <Text>{trainProgress.status==='done' ? 'Training Complete!' : trainProgress.status==='error' ? 'Training Failed' : 'Training in Progress…'}</Text>
                  </Text>
                  {trainProgress.status==='running' && (
                    <Text style={{ fontSize:14, color:ML.sub, marginTop:6 }}>
                      Epoch {trainProgress.epoch} / {trainParams.epochs}
                    </Text>
                  )}
                </View>
                {trainProgress.status !== 'error' && (
                  <View style={{ width:'100%', marginBottom:20 }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:8 }}>
                      <Text style={{ fontSize:13, color:ML.sub }}>Progress</Text>
                      <Text style={{ fontSize:13, fontWeight:'800', color:ML.primary }}>{trainProgress.pct}%</Text>
                    </View>
                    <View style={{ height:14, backgroundColor:'#E2E8F0', borderRadius:7, overflow:'hidden' }}>
                      <View style={{ width:`${trainProgress.pct}%`, height:'100%', borderRadius:7,
                        backgroundColor:trainProgress.status==='done'?ML.success:ML.primary }} />
                    </View>
                  </View>
                )}
                {trainProgress.status==='done' && (
                  <TouchableOpacity onPress={() => setShowTraining(false)} style={{ width:'100%', borderRadius:12, overflow:'hidden' }}>
                    <LinearGradient colors={[ML.success,'#059669']} style={{ paddingVertical:14, alignItems:'center' }}>
                      <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Done</Text>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              <View>
                <Text style={{ fontSize:13, color:ML.sub, lineHeight:20, marginBottom:20 }}>
                  Configure parameters for retraining <Text style={{ fontWeight:'700', color:ML.dark }}>{selectedModel?.name}</Text>.
                </Text>
                {/* Dataset selector */}
                <View style={{ marginBottom:20 }}>
                  <Text style={S.fLabel}>Training Dataset</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:8 }}>
                    {datasets.filter(d=>d.status==='ready').map(d => {
                      const sel = trainParams.datasetId===d.id;
                      return (
                        <TouchableOpacity key={d.id} onPress={() => setTrainParams(p=>({...p,datasetId:d.id}))}
                          style={{ paddingHorizontal:12, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:sel?ML.primary:ML.line, backgroundColor:sel?ML.primary+'18':'#fff' }}>
                          <Text style={{ fontSize:13, fontWeight:sel?'700':'500', color:sel?ML.primary:ML.sub }}>{d.name}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>
                {/* Params */}
                <View style={{ marginBottom:20 }}>
                  <Text style={S.fLabel}>Hyperparameters</Text>
                  {[
                    { label:'Epochs',           key:'epochs',          float:false },
                    { label:'Batch Size',        key:'batchSize',       float:false },
                    { label:'Learning Rate',     key:'learningRate',    float:true  },
                    { label:'Validation Split',  key:'validationSplit', float:true  },
                  ].map(p => (
                    <View key={p.key} style={{ flexDirection:'row', alignItems:'center',
                                               justifyContent:'space-between', paddingVertical:10,
                                               borderBottomWidth:1, borderBottomColor:'#F1F5F9' }}>
                      <Text style={{ fontSize:13, color:ML.sub }}>{p.label}</Text>
                      <TextInput
                        style={{ width:100, backgroundColor:'#F8FAFC', borderRadius:8, paddingHorizontal:10,
                                 paddingVertical:8, fontSize:14, color:ML.dark, borderWidth:1,
                                 borderColor:ML.line, textAlign:'center' }}
                        value={String(trainParams[p.key])}
                        onChangeText={v => setTrainParams(prev => ({
                          ...prev, [p.key]: p.float ? parseFloat(v)||0 : parseInt(v)||0
                        }))}
                        keyboardType="numeric"
                      />
                    </View>
                  ))}
                </View>
                {/* Optimizer */}
                <View style={{ marginBottom:20 }}>
                  <Text style={S.fLabel}>Optimizer</Text>
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                    {OPTIMIZERS.map(o => {
                      const sel = trainParams.optimizer===o;
                      return (
                        <TouchableOpacity key={o} onPress={() => setTrainParams(p=>({...p,optimizer:o}))}
                          style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:sel?ML.primary:ML.line, backgroundColor:sel?ML.primary+'18':'#fff' }}>
                          <Text style={{ fontSize:13, fontWeight:sel?'700':'500', color:sel?ML.primary:ML.sub }}>{o}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
                {/* Options */}
                <View style={{ marginBottom:20 }}>
                  {[
                    { label:'Enable Early Stopping', key:'useEarlyStopping' },
                    { label:'Data Augmentation',     key:'augmentData'      },
                  ].map(opt => (
                    <TouchableOpacity key={opt.key} onPress={() => setTrainParams(p=>({...p,[opt.key]:!p[opt.key]}))}
                      style={{ flexDirection:'row', alignItems:'center', gap:12, marginBottom:14 }}>
                      <MaterialCommunityIcons
                        name={trainParams[opt.key]?'checkbox-marked':'checkbox-blank-outline'}
                        size={24} color={trainParams[opt.key]?ML.primary:ML.sub} />
                      <Text style={{ fontSize:13, color:ML.sub }}>{opt.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <View style={{ backgroundColor:'#EEF2FF', borderRadius:10, padding:12,
                               flexDirection:'row', gap:8, alignItems:'center' }}>
                  <MaterialCommunityIcons name="clock-outline" size={18} color={ML.primary} />
                  <Text style={{ fontSize:13, color:ML.primary }}>
                    Est. ~{Math.max(1,Math.round(trainParams.epochs*trainParams.batchSize/1800))}h training time
                  </Text>
                </View>
              </View>
            )}
          </ScrollView>

          {!trainProgress && (
            <View style={S.mActions}>
              <TouchableOpacity onPress={() => setShowTraining(false)} style={[S.mBtn, { flex:1 }]}>
                <Text style={{ color:ML.sub, fontWeight:'700' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={doTrain} style={[S.mBtn, S.mBtnPrimary, { flex:2 }]}>
                <MaterialCommunityIcons name="play" size={16} color="#fff" />
                <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Start Training</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );

  // ── PREDICTION MODAL ──────────────────────────────────────────────────────
  const renderPredictModal = () => (
    <Modal visible={showPrediction} animationType="slide" transparent onRequestClose={() => setShowPrediction(false)}>
      <View style={S.overlay}>
        <View style={S.sheet}>
          <LinearGradient colors={[selectedModel?.color||ML.primary,(selectedModel?.color||ML.primary)+'bb']} style={S.sheetHdr}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
                <MaterialCommunityIcons name="play-circle" size={24} color="#fff" />
                <View>
                  <Text style={{ color:'#fff', fontSize:16, fontWeight:'800' }}>Run Prediction</Text>
                  <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:12 }}>{selectedModel?.name}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setShowPrediction(false)}>
                <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
              </TouchableOpacity>
            </View>
          </LinearGradient>

          <ScrollView contentContainerStyle={{ padding:20, paddingBottom:30 }}>
            {/* Feature inputs */}
            {(selectedModel?.features||[]).length > 0 && (
              <View style={{ marginBottom:20 }}>
                <Text style={S.fLabel}>Feature Values</Text>
                {(selectedModel.features||[]).map((f,i) => (
                  <View key={i} style={{ marginBottom:12 }}>
                    <Text style={{ fontSize:12, color:ML.sub, marginBottom:5, fontWeight:'600' }}>
                      {f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </Text>
                    <TextInput
                      style={{ backgroundColor:'#F8FAFC', borderRadius:10, padding:12, fontSize:14, color:ML.dark,
                               borderWidth:1.5, borderColor:featureVals[f]?ML.primary+'55':ML.line }}
                      placeholder={`Enter ${f}`} placeholderTextColor="#94A3B8"
                      value={featureVals[f]||''}
                      onChangeText={v => setFeatureVals(p=>({...p,[f]:v}))}
                    />
                  </View>
                ))}
              </View>
            )}
            {/* JSON input */}
            <View style={{ marginBottom:20 }}>
              <Text style={S.fLabel}>Raw JSON Input (optional)</Text>
              <TextInput
                style={{ backgroundColor:'#F8FAFC', borderRadius:10, padding:14, fontSize:13, color:ML.dark,
                         borderWidth:1, borderColor:ML.line, minHeight:100, textAlignVertical:'top',
                         fontFamily:Platform.OS==='ios'?'Menlo':'monospace' }}
                placeholder={'{"feature1":"value1","feature2":"value2"}'}
                placeholderTextColor="#94A3B8"
                value={inputData} onChangeText={setInputData} multiline numberOfLines={4}
              />
            </View>
            {/* Result */}
            {predResult && (
              <View style={{ backgroundColor:ML.success+'10', borderRadius:14, padding:16,
                             borderWidth:1, borderColor:ML.success+'30' }}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:14 }}>
                  <MaterialCommunityIcons name="check-circle" size={24} color={ML.success} />
                  <Text style={{ fontSize:16, fontWeight:'800', color:ML.dark }}>Prediction Complete</Text>
                </View>
                <View style={{ flexDirection:'row', gap:10, marginBottom:12 }}>
                  <View style={{ flex:1, backgroundColor:'#fff', borderRadius:10, padding:12, alignItems:'center' }}>
                    <Text style={{ fontSize:24, fontWeight:'800',
                      color:predResult.confidence>=85?ML.success:predResult.confidence>=65?ML.warning:ML.danger }}>
                      {predResult.confidence}%
                    </Text>
                    <Text style={{ fontSize:11, color:ML.sub }}>Confidence</Text>
                    <View style={{ width:'100%', height:6, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden', marginTop:6 }}>
                      <View style={{ width:`${predResult.confidence}%`, height:'100%', borderRadius:3,
                        backgroundColor:predResult.confidence>=85?ML.success:predResult.confidence>=65?ML.warning:ML.danger }} />
                    </View>
                  </View>
                  <View style={{ flex:1, backgroundColor:'#fff', borderRadius:10, padding:12, alignItems:'center',
                                 justifyContent:'center' }}>
                    <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, textAlign:'center' }}>
                      {predResult.result}
                    </Text>
                    <Text style={{ fontSize:11, color:ML.sub, marginTop:4 }}>Result</Text>
                  </View>
                </View>
                <View style={{ backgroundColor:'#fff', borderRadius:10, padding:12, marginBottom:10 }}>
                  <Text style={{ fontSize:12, color:ML.sub, marginBottom:4, fontWeight:'700' }}>DETAILS</Text>
                  <Text style={{ fontSize:13, color:ML.dark }}>{predResult.details}</Text>
                  <Text style={{ fontSize:11, color:ML.sub, marginTop:6 }}>
                    {predResult.processingMs}ms · {new Date(predResult.timestamp).toLocaleTimeString('en-GB')}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => { setShowPrediction(false); setActiveTab('predictions'); }}
                  style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <Text style={{ fontSize:13, fontWeight:'700', color:ML.primary }}>View in Predictions History</Text>
                  <Ionicons name="arrow-forward" size={14} color={ML.primary} />
                </TouchableOpacity>
                {routeInnovation && (
                  <TouchableOpacity onPress={() => { setShowPrediction(false); navigation?.navigate('InnovativeScreen'); }}
                    style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, marginTop:10 }}>
                    <Ionicons name="bulb-outline" size={14} color="#b8860b" />
                    <Text style={{ fontSize:13, fontWeight:'700', color:'#b8860b' }}>Back to Innovation Hub</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </ScrollView>

          <View style={S.mActions}>
            <TouchableOpacity onPress={() => { setPredResult(null); setInputData(''); setFeatureVals({}); }}
              style={[S.mBtn, { flex:1 }]}>
              <Text style={{ color:ML.sub, fontWeight:'700' }}>Clear</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={doPredict} disabled={predLoading}
              style={[S.mBtn, S.mBtnPrimary, { flex:2, opacity:predLoading?0.7:1 }]}>
              {predLoading ? <ActivityIndicator color="#fff" size="small" />
                : <MaterialCommunityIcons name="play" size={16} color="#fff" />}
              <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>
                <Text>{predLoading?'Running…':predResult?'Run Again':'Generate Prediction'}</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ── CREATE MODEL WIZARD ────────────────────────────────────────────────────
  const renderCreateModal = () => {
    const STEPS = ['Basics','Architecture','Settings'];
    const upd = (k,v) => setNewModel(p=>({...p,[k]:v}));
    return (
      <Modal visible={showCreate} animationType="slide" transparent onRequestClose={() => setShowCreate(false)}>
        <View style={S.overlay}>
          <View style={S.sheet}>
            <LinearGradient colors={[ML.primary,ML.secondary]} style={S.sheetHdr}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
                <View>
                  <Text style={{ color:'#fff', fontSize:17, fontWeight:'800' }}>Create New Model</Text>
                  <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:12, marginTop:2 }}>
                    Step {createStep} of {STEPS.length} — {STEPS[createStep-1]}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setShowCreate(false)}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
              <View style={{ flexDirection:'row', gap:6, marginTop:14 }}>
                {STEPS.map((s,i) => {
                  const n=i+1; const done=createStep>n; const active=createStep===n;
                  return (
                    <View key={n} style={{ flex:1, alignItems:'center', gap:4 }}>
                      <View style={{ height:4, width:'100%', borderRadius:2,
                        backgroundColor:done?'#fdb913':active?'#fff':'rgba(255,255,255,0.25)' }} />
                      <Text style={{ fontSize:10, fontWeight:'700',
                        color:done?'#fdb913':active?'#fff':'rgba(255,255,255,0.4)' }}>{s}</Text>
                    </View>
                  );
                })}
              </View>
            </LinearGradient>

            <ScrollView contentContainerStyle={{ padding:20, paddingBottom:30 }}>
              {/* STEP 1 */}
              {createStep===1 && (
                <View>
                  <View style={{ marginBottom:18 }}>
                    <Text style={S.fLabel}>Model Name <Text style={{ color:ML.danger }}>*</Text></Text>
                    <TextInput style={S.fInput} placeholder="e.g. Salary Range Predictor"
                      placeholderTextColor="#94A3B8" value={newModel.name} onChangeText={v=>upd('name',v)} />
                  </View>
                  <View style={{ marginBottom:18 }}>
                    <Text style={S.fLabel}>Description</Text>
                    <TextInput style={[S.fInput,{minHeight:80,textAlignVertical:'top'}]}
                      placeholder="Describe what this model does…" placeholderTextColor="#94A3B8"
                      value={newModel.description} onChangeText={v=>upd('description',v)} multiline />
                  </View>
                  <View style={{ marginBottom:18 }}>
                    <Text style={S.fLabel}>Model Type</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {MODEL_TYPES.map(t => { const sel=newModel.type===t.id; return (
                        <TouchableOpacity key={t.id} onPress={()=>upd('type',t.id)}
                          style={{ paddingHorizontal:12, paddingVertical:9, borderRadius:22, borderWidth:1.5,
                                   borderColor:sel?t.color:ML.line, backgroundColor:sel?t.color+'18':'#fff',
                                   flexDirection:'row', alignItems:'center', gap:5 }}>
                          <MaterialCommunityIcons name={t.icon} size={13} color={sel?t.color:ML.sub} />
                          <Text style={{ fontSize:12, fontWeight:sel?'700':'500', color:sel?t.color:ML.sub }}>{t.name}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:18 }}>
                    <Text style={S.fLabel}>Category</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {MODEL_CATEGORIES.map(c => { const sel=newModel.category===c.id; return (
                        <TouchableOpacity key={c.id} onPress={()=>upd('category',c.id)}
                          style={{ paddingHorizontal:12, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:sel?c.color:ML.line, backgroundColor:sel?c.color+'18':'#fff' }}>
                          <Text style={{ fontSize:12, fontWeight:sel?'700':'500', color:sel?c.color:ML.sub }}>{c.name}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:18 }}>
                    <Text style={S.fLabel}>Input Features (comma-separated)</Text>
                    <TextInput style={S.fInput} placeholderTextColor="#94A3B8"
                      placeholder="e.g. age, salary, experience_years, location"
                      value={newModel.features} onChangeText={v=>upd('features',v)} />
                  </View>
                </View>
              )}

              {/* STEP 2 */}
              {createStep===2 && (
                <View>
                  {[
                    { label:'Epochs',          key:'epochs',          isFloat:false },
                    { label:'Batch Size',       key:'batchSize',       isFloat:false },
                    { label:'Learning Rate',    key:'learningRate',    isFloat:true  },
                    { label:'Dropout Rate',     key:'dropout',         isFloat:true  },
                    { label:'Validation Split', key:'validationSplit', isFloat:true  },
                  ].map(f => (
                    <View key={f.key} style={{ marginBottom:16 }}>
                      <Text style={S.fLabel}>{f.label}</Text>
                      <TextInput style={S.fInput} value={String(newModel[f.key])}
                        onChangeText={v=>upd(f.key,f.isFloat?parseFloat(v)||0:parseInt(v)||0)}
                        keyboardType="numeric" />
                    </View>
                  ))}
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Optimizer</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {OPTIMIZERS.map(o=>{const s=newModel.optimizer===o;return(
                        <TouchableOpacity key={o} onPress={()=>upd('optimizer',o)}
                          style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:s?ML.primary:ML.line, backgroundColor:s?ML.primary+'18':'#fff' }}>
                          <Text style={{ fontSize:13, fontWeight:s?'700':'500', color:s?ML.primary:ML.sub }}>{o}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Activation Function</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {ACTIVATIONS.map(a=>{const s=newModel.activation===a;return(
                        <TouchableOpacity key={a} onPress={()=>upd('activation',a)}
                          style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:s?ML.secondary:ML.line, backgroundColor:s?ML.secondary+'18':'#fff' }}>
                          <Text style={{ fontSize:13, fontWeight:s?'700':'500', color:s?ML.secondary:ML.sub }}>{a}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                </View>
              )}

              {/* STEP 3 */}
              {createStep===3 && (
                <View>
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Loss Function</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {LOSS_FNS.map(l=>{const s=newModel.lossFunction===l;return(
                        <TouchableOpacity key={l} onPress={()=>upd('lossFunction',l)}
                          style={{ paddingHorizontal:12, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:s?ML.primary:ML.line, backgroundColor:s?ML.primary+'18':'#fff' }}>
                          <Text style={{ fontSize:12, fontWeight:s?'700':'500', color:s?ML.primary:ML.sub }}>{l}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Tracking Metrics</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {METRICS_LIST.map(m=>{const s=newModel.selectedMetrics.includes(m);return(
                        <TouchableOpacity key={m} onPress={()=>upd('selectedMetrics',s?newModel.selectedMetrics.filter(x=>x!==m):[...newModel.selectedMetrics,m])}
                          style={{ paddingHorizontal:12, paddingVertical:7, borderRadius:20, borderWidth:1.5,
                                   borderColor:s?ML.success:ML.line, backgroundColor:s?ML.success+'18':'#fff',
                                   flexDirection:'row', alignItems:'center', gap:4 }}>
                          {s && <MaterialCommunityIcons name="check" size={11} color={ML.success} />}
                          <Text style={{ fontSize:12, fontWeight:s?'700':'500', color:s?ML.success:ML.sub }}>{m}</Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Retrain Schedule</Text>
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                      {['manual','daily','weekly','monthly','quarterly'].map(r=>{const s=newModel.retrainSchedule===r;return(
                        <TouchableOpacity key={r} onPress={()=>upd('retrainSchedule',r)}
                          style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20, borderWidth:1.5,
                                   borderColor:s?ML.info:ML.line, backgroundColor:s?ML.info+'18':'#fff' }}>
                          <Text style={{ fontSize:12, fontWeight:s?'700':'500', color:s?ML.info:ML.sub }}>
                            {r.charAt(0).toUpperCase()+r.slice(1)}
                          </Text>
                        </TouchableOpacity>
                      );})}
                    </View>
                  </View>
                  <View style={{ marginBottom:16 }}>
                    <Text style={S.fLabel}>Alert Threshold (%)</Text>
                    <TextInput style={S.fInput} value={String(newModel.alertThreshold)}
                      onChangeText={v=>upd('alertThreshold',parseInt(v)||85)} keyboardType="numeric" />
                    <Text style={{ fontSize:12, color:ML.sub, marginTop:4 }}>
                      Trigger alert if accuracy drops below this level
                    </Text>
                  </View>
                </View>
              )}
            </ScrollView>

            <View style={S.mActions}>
              {createStep>1 && (
                <TouchableOpacity onPress={()=>setCreateStep(s=>s-1)} style={[S.mBtn,{flex:1}]}>
                  <Ionicons name="arrow-back" size={16} color={ML.sub} />
                  <Text style={{ color:ML.sub, fontWeight:'700' }}>Back</Text>
                </TouchableOpacity>
              )}
              {createStep<3 ? (
                <TouchableOpacity onPress={() => {
                  if (createStep===1 && !newModel.name.trim()) { Alert.alert('Required','Enter a model name.'); return; }
                  setCreateStep(s=>s+1);
                }} style={[S.mBtn, S.mBtnPrimary, { flex:2 }]}>
                  <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Continue</Text>
                  <Ionicons name="arrow-forward" size={16} color="#fff" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity onPress={doCreateModel} style={[S.mBtn, S.mBtnPrimary, { flex:2 }]}>
                  <MaterialCommunityIcons name="plus-circle" size={16} color="#fff" />
                  <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Create Model</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ── DATASET DETAIL MODAL ──────────────────────────────────────────────────
  const renderDatasetModal = () => {
    const ds = selDataset;
    if (!ds) return null;
    const qc = ds.quality>=90?ML.success:ds.quality>=75?ML.warning:ML.danger;
    return (
      <Modal visible={showDataset} animationType="slide" transparent onRequestClose={() => setShowDataset(false)}>
        <View style={S.overlay}>
          <View style={S.sheet}>
            <LinearGradient colors={[ML.primary, ML.info]} style={S.sheetHdr}>
              <View style={{ flexDirection:'row', justifyContent:'space-between' }}>
                <View style={{ flex:1 }}>
                  <Text style={{ color:'#fff', fontSize:17, fontWeight:'800' }}>{ds.name}</Text>
                  <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:12, marginTop:2 }}>{ds.description}</Text>
                </View>
                <TouchableOpacity onPress={() => setShowDataset(false)}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
            </LinearGradient>
            <ScrollView contentContainerStyle={{ padding:20, paddingBottom:40 }}>
              <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:20 }}>
                {[
                  { label:'Rows',     value:(ds.size||0).toLocaleString(), color:ML.primary },
                  { label:'Features', value:String(ds.features||'—'),     color:ML.info    },
                  { label:'Format',   value:ds.format||'—',               color:ML.dark    },
                  { label:'Source',   value:ds.source||'—',               color:ML.dark    },
                ].map((s,i) => (
                  <View key={i} style={{ width:(width-72)/2, backgroundColor:'#F8FAFC', borderRadius:12,
                                         padding:14, borderWidth:1, borderColor:ML.line, alignItems:'center' }}>
                    <Text style={{ fontSize:20, fontWeight:'800', color:s.color }}>{s.value}</Text>
                    <Text style={{ fontSize:11, color:ML.sub, marginTop:4 }}>{s.label}</Text>
                  </View>
                ))}
              </View>
              <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:10 }}>Data Quality</Text>
              <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:16,
                             borderWidth:1, borderColor:ML.line }}>
                <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:10 }}>
                  <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark }}>Overall Score</Text>
                  <Text style={{ fontSize:20, fontWeight:'800', color:qc }}>{ds.quality}%</Text>
                </View>
                <View style={{ height:10, backgroundColor:'#F1F5F9', borderRadius:5, overflow:'hidden', marginBottom:14 }}>
                  <View style={{ width:`${ds.quality}%`, height:'100%', backgroundColor:qc, borderRadius:5 }} />
                </View>
                {[
                  { label:'Completeness', value:Math.round(ds.quality*0.97) },
                  { label:'Consistency',  value:Math.round(ds.quality*0.99) },
                  { label:'Accuracy',     value:Math.round(ds.quality*0.96) },
                  { label:'Uniqueness',   value:Math.min(100,Math.round(ds.quality*1.01)) },
                ].map((q,i) => (
                  <View key={i} style={{ marginBottom:10 }}>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:3 }}>
                      <Text style={{ fontSize:12, color:ML.sub }}>{q.label}</Text>
                      <Text style={{ fontSize:12, fontWeight:'700', color:q.value>=90?ML.success:ML.warning }}>{q.value}%</Text>
                    </View>
                    <View style={{ height:6, backgroundColor:'#F1F5F9', borderRadius:3, overflow:'hidden' }}>
                      <View style={{ width:`${q.value}%`, height:'100%', borderRadius:3,
                        backgroundColor:q.value>=90?ML.success:ML.warning }} />
                    </View>
                  </View>
                ))}
              </View>
              {ds.linkedModels?.length > 0 && (
                <View>
                  <Text style={{ fontSize:14, fontWeight:'800', color:ML.dark, marginBottom:10 }}>Linked Models</Text>
                  {ds.linkedModels.map(id => {
                    const m = mlModels.find(m=>m.id===id);
                    return m ? (
                      <TouchableOpacity key={id} onPress={() => { setShowDataset(false); openDetail(m); }}
                        style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#F8FAFC',
                                 borderRadius:10, padding:12, marginBottom:8, borderWidth:1, borderColor:ML.line }}>
                        <MaterialCommunityIcons name={m.icon} size={20} color={m.color} />
                        <Text style={{ flex:1, fontSize:13, fontWeight:'600', color:ML.dark }}>{m.name}</Text>
                        <Ionicons name="chevron-forward" size={16} color={ML.sub} />
                      </TouchableOpacity>
                    ) : null;
                  })}
                </View>
              )}
              {ds.lastUpdated && (
                <Text style={{ fontSize:12, color:ML.sub, marginTop:8 }}>
                  Updated: {fmtDate(ds.lastUpdated, {weekday:'short',day:'numeric',month:'long',year:'numeric'})}
                </Text>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ── ALERTS MODAL ──────────────────────────────────────────────────────────
  const renderAlertsModal = () => (
    <Modal visible={showAlerts} animationType="slide" transparent onRequestClose={() => setShowAlerts(false)}>
      <View style={S.overlay}>
        <View style={S.sheet}>
          <LinearGradient colors={['#EF4444','#dc2626']} style={S.sheetHdr}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
                <MaterialCommunityIcons name="bell-ring" size={24} color="#fff" />
                <View>
                  <Text style={{ color:'#fff', fontSize:16, fontWeight:'800' }}>ML Alerts</Text>
                  <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:12 }}>
                    {unresolved.length} unresolved · {alerts.length} total
                  </Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setShowAlerts(false)}>
                <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
              </TouchableOpacity>
            </View>
          </LinearGradient>
          <ScrollView contentContainerStyle={{ padding:20, paddingBottom:40 }}>
            {alerts.map(alert => {
              const model = mlModels.find(m=>String(m.id)===String(alert.model_id));
              const sc = alert.severity==='high'?ML.danger:alert.severity==='medium'?ML.warning:ML.info;
              return (
                <View key={alert.id} style={[S.card, alert.resolved&&{opacity:0.55}, { marginBottom:10 }]}>
                  <View style={{ padding:14, flexDirection:'row', gap:10 }}>
                    <View style={{ width:38, height:38, borderRadius:19, backgroundColor:sc+'18',
                                   alignItems:'center', justifyContent:'center' }}>
                      <MaterialCommunityIcons name={
                        alert.severity==='high'?'alert-circle':alert.severity==='medium'?'alert':'information'
                      } size={20} color={sc} />
                    </View>
                    <View style={{ flex:1 }}>
                      <View style={{ flexDirection:'row', gap:6, flexWrap:'wrap', marginBottom:5 }}>
                        <View style={{ backgroundColor:sc+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                          <Text style={{ fontSize:10, fontWeight:'800', color:sc }}>{String(alert.severity ?? '').toUpperCase()}</Text>
                        </View>
                        <View style={{ backgroundColor:'#F1F5F9', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                          <Text style={{ fontSize:10, fontWeight:'600', color:ML.sub }}>{alert.type.replace('_',' ')}</Text>
                        </View>
                        {alert.resolved && (
                          <View style={{ backgroundColor:ML.success+'18', borderRadius:6, paddingHorizontal:7, paddingVertical:2 }}>
                            <Text style={{ fontSize:10, fontWeight:'800', color:ML.success }}>RESOLVED</Text>
                          </View>
                        )}
                      </View>
                      <Text style={{ fontSize:13, fontWeight:'700', color:ML.dark, marginBottom:3 }}>
                        {model?.name||'Unknown Model'}
                      </Text>
                      <Text style={{ fontSize:12, color:ML.sub, lineHeight:17 }}>{alert.message}</Text>
                      <Text style={{ fontSize:11, color:ML.sub, marginTop:4 }}>{fmtDateTime(alert.timestamp)}</Text>
                      {!alert.resolved && (
                        <View style={{ flexDirection:'row', gap:8, marginTop:10 }}>
                          <TouchableOpacity onPress={() => resolveAlert(alert.id)}
                            style={{ backgroundColor:ML.success+'18', borderRadius:8, paddingHorizontal:12, paddingVertical:6 }}>
                            <Text style={{ fontSize:12, fontWeight:'700', color:ML.success }}>Resolve</Text>
                          </TouchableOpacity>
                          {alert.type==='drift' && (
                            <TouchableOpacity onPress={() => {
                                setShowAlerts(false);
                                const m = mlModels.find(m=>String(m.id)===String(alert.model_id));
                                if (m) openTrain(m);
                              }}
                              style={{ backgroundColor:ML.warning+'18', borderRadius:8, paddingHorizontal:12, paddingVertical:6 }}>
                              <Text style={{ fontSize:12, fontWeight:'700', color:ML.warning }}>Retrain Now</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (isLoading) return (
    <View style={{ flex:1, justifyContent:'center', alignItems:'center', backgroundColor:ML.bg }}>
      <LinearGradient colors={[ML.primary,ML.secondary]}
        style={{ width:80, height:80, borderRadius:40, alignItems:'center', justifyContent:'center', marginBottom:20 }}>
        <MaterialCommunityIcons name="brain" size={40} color="#fff" />
      </LinearGradient>
      <ActivityIndicator size={36} color={ML.primary} />
      <Text style={{ fontSize:18, fontWeight:'700', color:ML.dark, marginTop:16 }}>Loading ML Platform…</Text>
      <Text style={{ fontSize:14, color:ML.sub, marginTop:6 }}>
        <Text>{dbConnected ? 'Connecting to database…' : 'Loading offline data…'}</Text>
      </Text>
    </View>
  );

  // ── MAIN RENDER ────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={{ flex:1, backgroundColor:ML.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={ML.primary} />
      <Animated.View style={{ flex:1, opacity:fadeAnim }}>
        <ScrollView showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[ML.primary]} tintColor={ML.primary} />
          }>
          {renderHeader()}
          {renderTabBar()}
          {activeTab==='models'      && renderModels()}
          {activeTab==='training'    && renderTraining()}
          {activeTab==='predictions' && renderPredictions()}
          {activeTab==='datasets'    && renderDatasets()}
          {activeTab==='analytics'   && renderAnalytics()}
          {activeTab==='monitoring'  && renderMonitoring()}
        </ScrollView>
      </Animated.View>

      {renderDetailsModal()}
      {renderTrainModal()}
      {renderPredictModal()}
      {renderCreateModal()}
      {renderDatasetModal()}
      {renderAlertsModal()}
    </SafeAreaView>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  iconBtn:   { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' },
  badge:     { position:'absolute', top:-3, right:-3, width:16, height:16, borderRadius:8, backgroundColor:ML.danger, alignItems:'center', justifyContent:'center' },
  badgeTxt:  { fontSize:9, color:'#fff', fontWeight:'900' },
  card:      { backgroundColor:'#fff', borderRadius:16, marginBottom:14, borderWidth:1, borderColor:ML.line, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.07, shadowRadius:6, elevation:3 },
  cardBtn:   { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, paddingVertical:10, borderRadius:10, borderWidth:1.5 },
  overlay:   { flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' },
  sheet:     { backgroundColor:ML.bg, borderTopLeftRadius:28, borderTopRightRadius:28, height:height*0.94 },
  sheetHdr:  { borderTopLeftRadius:28, borderTopRightRadius:28, padding:20, paddingBottom:16 },
  mActions:  { flexDirection:'row', gap:10, padding:16, borderTopWidth:1, borderTopColor:ML.line, backgroundColor:'#fff' },
  mBtn:      { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, paddingVertical:14, borderRadius:14, borderWidth:1.5, borderColor:ML.line },
  mBtnPrimary: { backgroundColor:ML.primary, borderColor:ML.primary },
  fLabel:    { fontSize:13, fontWeight:'700', color:ML.dark, marginBottom:8 },
  fInput:    { backgroundColor:'#fff', borderRadius:12, paddingHorizontal:14, paddingVertical:12, fontSize:14, color:ML.dark, borderWidth:1.5, borderColor:ML.line },
});